/**
 * BALLET DRILLS DATA STRUCTURE - CONSOLIDATED
 *
 * This file contains consolidated ballet drills organized into skill levels:
 * 1. Beginner Ballet Basics (30 minutes) - Basic ballet positions and movements
 * 2. Intermediate Ballet Basics (45 minutes) - More ballet movements and combinations
 * 3. Advanced Ballet Basics (45 minutes) - Challenging ballet movements and coordination
 * 4. Beginner Ballet Movement (30 minutes) - Basic ballet steps and coordination
 * 5. Intermediate Ballet Movement (45 minutes) - More complex ballet steps
 * 6. Advanced Ballet Movement (45 minutes) - Advanced ballet steps and combinations
 * 7. Beginner Ballet Grace (30 minutes) - Basic grace and flow movements
 * 8. Intermediate Ballet Grace (45 minutes) - More graceful movements and combinations
 * 9. Advanced Ballet Grace (45 minutes) - Advanced grace and flow techniques
 * 10. Complete Ballet (60 minutes) - All ballet skills combined
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 11001 (ballet drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'ballet'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Basics', 'Movement', 'Grace')
 * - points: Point value for completion (30-60 points based on time)
 * - instructions: Detailed explanation including drill name introduction
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (1800-3600 seconds = 30-60 minutes)
 * - equipment: Required equipment (e.g., 'None' or 'Chair for balance')
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains drill name introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-M (male Studio voice that supports SSML)
 *
 * BALLET DRILL REQUIREMENTS:
 * - All drills doable at home/indoors with minimal space
 * - Focus on individual skills (no partner drills)
 * - Equipment limited to optional chair for balance
 * - Appropriate for 6-10 year olds
 * - Focus on fundamental ballet movements and coordination
 * - Progressive skill development within each drill
 * - Emphasis on proper form and technique
 * - Kid-friendly language (no French ballet terms)
 * - Safe movements only
 *
 * CREATED: [Date] - Ballet drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with longer drills
 */

const balletDrills = [
  // BEGINNER BALLET BASICS (30 minutes) Done
  {
    id: 2190,
    name: 'Beginner Ballet Basics',
    categories: ['Basics', 'beginner'],
    sports: ['ballet'],
    difficulty: 'beginner',
    skill: 'Basics',
    points: 30,
    instructions:
      'Beginner Ballet Basics teaches you simple ballet positions and movements. This 30-minute session helps you learn basic ballet moves, improve your balance, and practice graceful movements.',
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, ballet stance
      {
        time: 0,
        text: 'Stand tall in Mountain. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Posture definition. Tall spine, chest calm, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 24,
        text: 'Turnout safety. Toes turn out a little from your hips like small pizza slices. Knees point the same way as toes.',
      },
      {
        time: 36,
        text: 'Arms ready. Fingers long like you hold bubbles. Wrists soft, elbows gently rounded.',
      },
      {
        time: 48,
        text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1. Quiet feet.',
      },
      { time: 60, text: 'Ankles roll. Right two, left two. Knees stay soft.' },
      { time: 72, text: 'Neck long. Shoulders down. Small smile.' },
      {
        time: 84,
        text: 'Stand tall. Hold still for 3, 2, 1. Ready for ballet.',
      },
      {
        time: 96,
        text: 'Hands to low arms. This is bras bas, low arms like a gentle circle.',
      },
      {
        time: 108,
        text: 'Posture reminder. Tall spine, chin level, shoulders down.',
      },
      { time: 120, text: 'Turnout is small and safe. Knees and toes match.' },
      { time: 132, text: 'Breathe steady. We begin.' },

      // 3:00-6:00 Feet positions: first and second
      {
        time: 144,
        text: 'First position definition. Heels together, toes turn out a little. Knees over toes, spine tall.',
      },
      {
        time: 156,
        text: 'Stand in first position. Hold for 3, 2, 1. Shoulders down.',
      },
      {
        time: 168,
        text: 'Demi plie definition. Small knee bend, heels stay down, knees track over toes, spine tall.',
      },
      {
        time: 180,
        text: 'Do two demi plies in first. Bend small, stretch tall.',
      },
      {
        time: 192,
        text: 'Second position definition. Step one foot to the side, feet wide, toes out a little, knees over toes.',
      },
      { time: 204, text: 'Stand in second. Hold for 3, 2, 1. Arms relaxed.' },
      {
        time: 216,
        text: 'Demi plie in second. Bend small, press knees over toes, stretch tall.',
      },
      {
        time: 228,
        text: 'Repeat demi plie in second one more time. Quiet feet.',
      },
      {
        time: 240,
        text: 'Return to first position. Heels together, toes out a little.',
      },
      {
        time: 252,
        text: 'Posture check. Tall spine, tummy snug, shoulders down.',
      },
      { time: 264, text: 'Breathe in, breathe out. Hold first for 3, 2, 1.' },
      {
        time: 276,
        text: 'Turnout reminder. Small from hips, knees and toes match.',
      },

      // 6:00-9:00 Arms positions and port de bras
      {
        time: 288,
        text: 'Arms first position. Lift arms to a gentle circle in front of your belly. Fingers long.',
      },
      {
        time: 300,
        text: 'Arms second position. Open arms wide to the sides, elbows soft, palms face forward.',
      },
      {
        time: 312,
        text: 'Arms fifth position. Lift arms overhead in a soft circle. Shoulders stay down.',
      },
      {
        time: 324,
        text: 'Port de bras. Bras bas to first to second to fifth, then down to bras bas. Slow and smooth.',
      },
      { time: 336, text: 'Repeat port de bras again. Breathe with the arms.' },
      { time: 348, text: 'Reminder. Neck long, shoulders down, fingers soft.' },
      { time: 360, text: 'Hold first position of arms for 3, 2, 1. Smile.' },
      { time: 372, text: 'Open to second for 3, 2, 1. Breathe out.' },
      { time: 384, text: 'Lift to fifth for 3, 2, 1. Shoulders down.' },
      { time: 396, text: 'Lower to bras bas. Calm breath.' },
      { time: 408, text: 'Posture check. Tall spine, chin level.' },
      { time: 420, text: 'Ready for tendu.' },

      // 9:00-12:00 Tendu basics
      {
        time: 432,
        text: 'Tendu definition. Stretch the working foot along the floor to a point, heel forward, toes long, then close.',
      },
      {
        time: 444,
        text: 'From first. Tendu front with right foot, close to first. Then left, close.',
      },
      {
        time: 456,
        text: 'Repeat tendu front right and left. Hips steady, knees straight.',
      },
      {
        time: 468,
        text: 'Tendu side definition. Brush to the side, heel forward, toes long, close to first.',
      },
      {
        time: 480,
        text: 'Tendu side right, close. Tendu side left, close. Small and clean.',
      },
      {
        time: 492,
        text: 'Tendu back definition. Brush foot behind, toes touch floor, heel up, close to first.',
      },
      { time: 504, text: 'Tendu back right, close. Tendu back left, close.' },
      {
        time: 516,
        text: 'Reminder. Knees straight, toes long, heel leads front and side.',
      },
      {
        time: 528,
        text: 'Tendu front, side, back with right. Close each time.',
      },
      {
        time: 540,
        text: 'Tendu front, side, back with left. Close each time.',
      },
      { time: 552, text: 'Hold first for 3, 2, 1. Shoulders down.' },
      { time: 564, text: 'Breathe steady. Quiet feet.' },

      // 12:00-15:00 Degage and point flex
      {
        time: 576,
        text: 'Degage definition. Brush the foot off the floor a little, toes point, then close to first.',
      },
      {
        time: 588,
        text: 'From first. Degage front right, close. Degage front left, close.',
      },
      { time: 600, text: 'Degage side right, close. Degage side left, close.' },
      {
        time: 612,
        text: 'Point and flex definition. Point toes long, then flex toes up, knee straight.',
      },
      {
        time: 624,
        text: 'Point and flex right foot in front, two times, then close.',
      },
      {
        time: 636,
        text: 'Point and flex left foot in front, two times, then close.',
      },
      { time: 648, text: 'Reminder. Knees straight, toes long, hips still.' },
      { time: 660, text: 'Degage back right, close. Degage back left, close.' },
      { time: 672, text: 'Hold first. Breathe in, breathe out.' },
      {
        time: 684,
        text: 'Posture check. Tall spine, chin level, shoulders down.',
      },
      { time: 696, text: 'Ready for releve.' },
      { time: 708, text: 'Quiet feet. Calm breath.' },

      // 15:00-18:00 Releve and balance
      {
        time: 720,
        text: 'Releve definition. Rise onto the balls of the feet, heels up, knees straight, spine tall, then lower with control.',
      },
      {
        time: 732,
        text: 'From first. Releve up and down two times. Arms in second.',
      },
      {
        time: 744,
        text: 'From second. Step to second, releve up and down two times. Knees straight.',
      },
      {
        time: 756,
        text: 'Balance definition. Hold releve and still body for 3, 2, 1. Breathe steady.',
      },
      {
        time: 768,
        text: 'Balance in first on releve for 3, 2, 1. Lower with control.',
      },
      {
        time: 780,
        text: 'Passé definition. Toe to knee, knee opens to the side, stand tall.',
      },
      {
        time: 792,
        text: 'Passé right foot to knee, hold for 3, 2, 1, lower to first.',
      },
      {
        time: 804,
        text: 'Passé left foot to knee, hold for 3, 2, 1, lower to first.',
      },
      { time: 816, text: 'Reminder. Tall spine, tummy snug, shoulders down.' },
      { time: 828, text: 'Releve in first one time. Lower slow.' },
      { time: 840, text: 'Hold first for 3, 2, 1. Quiet feet.' },
      { time: 852, text: 'Breathe steady. Small smile.' },

      // 18:00-21:00 Plié and port de bras combo
      {
        time: 864,
        text: 'Combo. First position demi plie, arms to first. Stretch, open arms to second.',
      },
      {
        time: 876,
        text: 'Repeat combo in first. Bend small, stretch tall, arms flow.',
      },
      {
        time: 888,
        text: 'Step to second. Demi plie in second, arms open, then lift to fifth, then down.',
      },
      { time: 900, text: 'Repeat plie in second with arms. Smooth and slow.' },
      {
        time: 912,
        text: 'Return to first. Posture check. Knees and toes match.',
      },
      {
        time: 924,
        text: 'Tendu front right, close, plie first, stretch. Then tendu front left, close, plie, stretch.',
      },
      {
        time: 936,
        text: 'Reminder. Heels stay down in demi plie. Spine tall.',
      },
      { time: 948, text: 'Hold first for 3, 2, 1. Breathe out.' },
      { time: 960, text: 'Arms to bras bas. Shoulders down.' },
      { time: 972, text: 'Calm breath. Quiet feet.' },
      { time: 984, text: 'Ready to travel in place.' },
      { time: 996, text: 'We will glide small and safe.' },

      // 21:00-24:00 Chassé and gallop in place
      {
        time: 1008,
        text: 'Chassé definition. Step, close, step, feet kiss in the middle, glide like a slide.',
      },
      {
        time: 1020,
        text: 'Chassé to the right small, then to the left small. Arms low.',
      },
      { time: 1032, text: 'Repeat chassé right and left. Quiet landings.' },
      {
        time: 1044,
        text: 'Gallop definition. Lead foot steps, back foot catches up, small and light.',
      },
      { time: 1056, text: 'Gallop right small, then left small. Knees soft.' },
      { time: 1068, text: 'Reminder. Two tracks, heels light, chest calm.' },
      {
        time: 1080,
        text: 'Chassé right, chassé left, then gallop right, gallop left.',
      },
      { time: 1092, text: 'Hold first for 3, 2, 1. Breathe steady.' },
      { time: 1104, text: 'Arms to second. Shoulders down.' },
      { time: 1116, text: 'Posture check. Tall spine.' },
      { time: 1128, text: 'Ready for tiny jumps.' },
      { time: 1140, text: 'We jump small and safe.' },

      // 24:00-27:00 Sauté small and échappé prep
      {
        time: 1152,
        text: 'Sauté definition. Tiny straight jump in place. Heels together in first, toes out, land soft, knees over toes.',
      },
      {
        time: 1164,
        text: 'Do four small sautés in first. Land quiet, bend small.',
      },
      { time: 1176, text: 'Hold first. Breathe in, breathe out.' },
      {
        time: 1188,
        text: 'Échappé prep definition. From first, jump to second and back to first, tiny and soft.',
      },
      {
        time: 1200,
        text: 'Échappé prep. Tiny jump to second, land soft, then first, land soft.',
      },
      {
        time: 1212,
        text: 'Repeat échappé prep one more time. Knees over toes.',
      },
      {
        time: 1224,
        text: 'Reminder. Land through toes, then heels, knees bend, spine tall.',
      },
      { time: 1236, text: 'Hold first for 3, 2, 1. Shoulders down.' },
      { time: 1248, text: 'Arms low. Calm breath.' },
      { time: 1260, text: 'If jumps feel tired, stand and breathe.' },
      { time: 1272, text: 'Quiet feet, small smile.' },
      { time: 1284, text: 'Ready to link a mini dance.' },

      // 27:00-29:00 Mini dance link
      {
        time: 1296,
        text: 'Mini dance. First position. Tendu front right, close. Tendu side right, close. Releve in first, lower.',
      },
      {
        time: 1308,
        text: 'Repeat left. Tendu front left, close. Tendu side left, close. Releve, lower.',
      },
      { time: 1320, text: 'Arms. Bras bas to first to second, then down.' },
      { time: 1332, text: 'Chassé right, chassé left. Hold first.' },
      { time: 1344, text: 'Repeat mini dance from the top. Smooth and light.' },
      { time: 1356, text: 'Reminder. Knees and toes match. Shoulders down.' },
      {
        time: 1368,
        text: 'Balance in first on releve for 3, 2, 1. Lower with control.',
      },
      { time: 1380, text: 'Hold first. Breathe steady.' },
      {
        time: 1392,
        text: 'Port de bras one time. Bras bas, first, second, fifth, down.',
      },
      { time: 1404, text: 'Stand tall. Small smile.' },
      { time: 1416, text: 'Ready to cool down.' },
      { time: 1428, text: 'Calm breath.' },

      // 29:00-30:00 Cooldown and reverence
      {
        time: 1440,
        text: 'Butterfly stretch. Sit tall, feet together, knees wide, hold ankles. Hold for 3, 2, 1.',
      },
      {
        time: 1452,
        text: 'Seated hamstring fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      { time: 1464, text: 'Roll to stand. Mountain. Tall spine.' },
      {
        time: 1476,
        text: 'Arms open to second. Breathe in. Arms down to bras bas. Breathe out.',
      },
      {
        time: 1488,
        text: 'Reverence definition. A little bow or curtsey to say thank you.',
      },
      { time: 1500, text: 'Do a small reverence. Hold for 3, 2, 1. Smile.' },
      {
        time: 1512,
        text: 'Posture scan. Tall spine, shoulders down, chin level.',
      },
      { time: 1524, text: 'Thank your body for dancing. Breathe in.' },
      { time: 1536, text: 'Breathe out. Quiet feet.' },
      { time: 1548, text: 'Stand tall in first. Hold for 3, 2, 1.' },
      { time: 1560, text: 'Calm breath in.' },
      { time: 1572, text: 'Calm breath out.' },
      { time: 1584, text: 'Hands to low arms. Shoulders soft.' },
      { time: 1596, text: 'Small smile. Eyes forward.' },
      { time: 1608, text: 'Two tracks, knees and toes match.' },
      { time: 1620, text: 'Neck long, tummy snug.' },
      { time: 1632, text: 'Quiet body for 3, 2, 1.' },
      { time: 1644, text: 'You danced with care today.' },
      { time: 1656, text: 'Stand proud. Breathe steady.' },
      { time: 1668, text: 'Arms open to second, then down.' },
      { time: 1680, text: 'Thank you, dancer. Hold still for 3, 2, 1.' },
      { time: 1692, text: 'Deep breath in.' },
      { time: 1704, text: 'Long breath out.' },
      { time: 1716, text: 'Hands by sides. Quiet feet.' },
      { time: 1728, text: 'Head over hips. Tall spine.' },
      { time: 1740, text: 'Calm face. Soft eyes.' },
      { time: 1752, text: 'Stand in first. Hold for 3, 2, 1.' },
      { time: 1764, text: 'Small smile. Breathe in.' },
      {
        time: 1776,
        text: 'Breathe out... Great work! Ballet basics complete.',
      },
    ],
    timeTotal: 1780,
    equipment: 'None',
  },

  // INTERMEDIATE BALLET BASICS (45 minutes) Done
  {
    id: 2191,
    name: 'Intermediate Ballet Basics',
    categories: ['Basics', 'intermediate'],
    sports: ['ballet'],
    difficulty: 'intermediate',
    skill: 'Basics',
    points: 23,
    instructions:
      'Intermediate Ballet Basics builds on your basic ballet skills with harder moves and better coordination. This 23-minute session helps you practice better ballet moves, fun combinations, and cool patterns.',
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, turnout safety
      {
        time: 0,
        text: 'Stand tall in Mountain. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Posture definition. Tall spine, chest calm, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 24,
        text: 'Turnout safety. Toes turn out a little from your hips like small pizza slices. Knees point the same way as toes.',
      },
      {
        time: 36,
        text: 'Arms ready. Fingers long, wrists soft, elbows rounded. Hold low arms in a gentle circle.',
      },
      {
        time: 48,
        text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1. Quiet feet.',
      },
      { time: 60, text: 'Ankles roll. Right two, left two. Knees stay soft.' },
      { time: 72, text: 'Neck long. Shoulders down. Small smile.' },
      {
        time: 84,
        text: 'Stand tall. Hold still for 3, 2, 1. Ready for ballet.',
      },
      {
        time: 96,
        text: 'Reminder. Turnout is small and safe. Knees and toes match.',
      },
      {
        time: 108,
        text: 'Posture scan. Tall spine, chin level, shoulders down.',
      },

      // 3:00-7:00 Positions and demi plié: first, second, fifth
      {
        time: 120,
        text: 'First position. Heels together, toes turn out a little. Knees over toes, spine tall.',
      },
      {
        time: 132,
        text: 'Demi plie in first. Bend small, heels down, stretch tall.',
      },
      { time: 144, text: 'Repeat demi plie in first. Knees track over toes.' },
      {
        time: 156,
        text: 'Second position. Step one foot to the side, feet wide, toes out a little.',
      },
      {
        time: 168,
        text: 'Demi plie in second. Bend small, press knees over toes, stretch tall.',
      },
      {
        time: 180,
        text: 'Repeat demi plie in second. Spine tall, heels down.',
      },
      {
        time: 192,
        text: 'Fifth position definition. Right foot front, heel to big toe, toes turned out, knees match toes.',
      },
      {
        time: 204,
        text: 'Stand in fifth small turnout. Hold for 3, 2, 1. Shoulders down.',
      },
      {
        time: 216,
        text: 'Demi plie in fifth. Bend small, keep hips square, stretch tall.',
      },
      { time: 228, text: 'Return to first. Breathe steady.' },

      // 7:00-11:00 Port de bras with positions (first, second, fifth)
      {
        time: 240,
        text: 'Arms first. Lift to gentle circle in front. Elbows rounded.',
      },
      {
        time: 252,
        text: 'Arms second. Open wide, elbows soft, palms forward.',
      },
      {
        time: 264,
        text: 'Arms fifth. Lift overhead in a soft circle. Shoulders stay down.',
      },
      {
        time: 276,
        text: 'Port de bras flow. Low arms to first to second to fifth, then down.',
      },
      { time: 288, text: 'Repeat port de bras. Breathe with the arms.' },
      { time: 300, text: 'Reminder. Neck long, shoulders down, fingers soft.' },
      { time: 312, text: 'Hold second for 3, 2, 1. Posture tall.' },
      { time: 324, text: 'Lower to low arms. Quiet feet.' },

      // 11:00-15:00 Tendu en croix with arm coordination
      {
        time: 336,
        text: 'Tendu definition. Brush the foot along the floor to a point, heel forward, toes long, then close.',
      },
      {
        time: 348,
        text: 'From first with arms in first. Tendu front right, close. Then left, close.',
      },
      {
        time: 360,
        text: 'Arms open to second. Tendu side right, close. Left, close.',
      },
      {
        time: 372,
        text: 'Arms low. Tendu back right, close. Left, close. Heel up in back.',
      },
      { time: 384, text: 'Reminder. Knees straight, toes long, hips quiet.' },
      {
        time: 396,
        text: 'En croix right: front, side, back, close. Arms second.',
      },
      {
        time: 408,
        text: 'En croix left: front, side, back, close. Arms second.',
      },
      { time: 420, text: 'Hold first. Shoulders down.' },

      // 15:00-18:00 Dégagé accents and point-flex
      {
        time: 432,
        text: 'Degage definition. Brush the foot off the floor a little, toes point, then close.',
      },
      {
        time: 444,
        text: 'Degage front right two, close. Degage front left two, close.',
      },
      { time: 456, text: 'Degage side right two, left two. Small and sharp.' },
      {
        time: 468,
        text: 'Point flex definition. Point toes long, then flex toes up, knee straight.',
      },
      {
        time: 480,
        text: 'Point flex right two, close. Point flex left two, close.',
      },
      { time: 492, text: 'Reminder. Hips steady, knees straight, toes long.' },

      // 18:00-21:00 Rond de jambe à terre (both directions)
      {
        time: 504,
        text: 'Rond de jambe definition. Draw a circle with your toe on the floor: front to side to back, then close.',
      },
      {
        time: 516,
        text: 'Right leg three circles outward. Front, side, back, close. Hips quiet.',
      },
      {
        time: 528,
        text: 'Right leg three circles inward. Back, side, front, close.',
      },
      { time: 540, text: 'Left leg three circles outward. Smooth and even.' },
      { time: 552, text: 'Left leg three circles inward. Breathe steady.' },
      { time: 564, text: 'Reminder. Spine tall, tummy snug, toes long.' },

      // 21:00-24:00 Fondu prep and développé prep to 45
      {
        time: 576,
        text: 'Fondu definition. Bend both knees, then stretch as the working leg lengthens. Small and smooth.',
      },
      {
        time: 588,
        text: 'Fondu front right two, close. Then left two, close.',
      },
      {
        time: 600,
        text: 'Developpe prep definition. Knee lifts to passé, then leg unfolds to about 45, then close.',
      },
      {
        time: 612,
        text: 'Developpe prep front right to 45, close. Then left, close. Safe and small.',
      },
      {
        time: 624,
        text: 'Reminder. Hips level, knee opens to the side, shoulders down.',
      },

      // 24:00-27:00 Relevé, échappé, and balances (passé)
      {
        time: 636,
        text: 'Releve definition. Rise onto balls of feet, heels up, knees straight, lower with control.',
      },
      {
        time: 648,
        text: 'From first. Releve up and down two times. Arms second.',
      },
      {
        time: 660,
        text: 'Echappe definition. From first, jump or rise to second, then return to first, land or lower soft.',
      },
      {
        time: 672,
        text: 'Echappe to second and back to first two times. Knees over toes.',
      },
      {
        time: 684,
        text: 'Passe definition. Toe to knee, knee opens to the side, stand tall.',
      },
      {
        time: 696,
        text: 'Passe balance right for 3, 2, 1, then lower. Passe left for 3, 2, 1, then lower.',
      },
      { time: 708, text: 'Reminder. Tall spine, tummy snug, shoulders down.' },

      // 27:00-30:00 Adagio center (port de bras with balances)
      {
        time: 720,
        text: 'Adagio flow. Arms low to first to second to fifth as you stand in first. Slow and smooth.',
      },
      {
        time: 732,
        text: 'Balance in first on releve for 3, 2, 1. Lower slow.',
      },
      {
        time: 744,
        text: 'Open arms to second, side bend right small, then left small.',
      },
      {
        time: 756,
        text: 'Lift arms to fifth, breathe in. Lower to low arms, breathe out.',
      },
      { time: 768, text: 'Reminder. Neck long, shoulders down, fingers soft.' },

      // 30:00-33:00 Pirouette prep and spotting
      {
        time: 780,
        text: 'Pirouette prep definition. From fourth position lunge, push to releve in passe, quarter turn, then close.',
      },
      {
        time: 792,
        text: 'Spotting definition. Pick a point with your eyes, turn your body, snap your head back to the point.',
      },
      {
        time: 804,
        text: 'Fourth prep right. Front foot forward, back foot turned out small. Push to releve, quarter turn, close.',
      },
      {
        time: 816,
        text: 'Repeat quarter turn right. Spot the point each time.',
      },
      {
        time: 828,
        text: 'Fourth prep left. Push to releve, quarter turn, close.',
      },
      { time: 840, text: 'Repeat quarter turn left. Spot the point.' },
      {
        time: 852,
        text: 'Reminder. Hips square, knees and toes match, shoulders down.',
      },

      // 33:00-36:00 Allegro small: chassé, glissade, assemblé
      {
        time: 864,
        text: 'Chasse definition. Step, close, step, feet kiss in the middle, glide like a slide.',
      },
      {
        time: 876,
        text: 'Chasse right small, then left small. Quiet landings.',
      },
      {
        time: 888,
        text: 'Glissade definition. Brush and glide to the side, feet close in the air, land in fifth.',
      },
      {
        time: 900,
        text: 'Glissade right small, then left small. Knees over toes.',
      },
      {
        time: 912,
        text: 'Assemble definition. Brush one foot, jump and assemble both feet together to land.',
      },
      {
        time: 924,
        text: 'Assemble right small, then left small. Land soft, bend small.',
      },
      {
        time: 936,
        text: 'Reminder. Land through toes, then heels, spine tall.',
      },

      // 36:00-39:00 Travel link: chassé and pas de bourrée, balancé
      {
        time: 948,
        text: 'Pas de bourree definition. Back, side, front steps, tiny triangle, light and quick.',
      },
      {
        time: 960,
        text: 'Chasse right, pas de bourree right. Then chasse left, pas de bourree left.',
      },
      {
        time: 972,
        text: 'Balance step definition. Side, back, front rocking step with arms open and soft.',
      },
      {
        time: 984,
        text: 'Balance right once, then left once. Smooth and musical.',
      },
      {
        time: 996,
        text: 'Repeat: chasse, pas de bourree, balance. Quiet feet.',
      },
      {
        time: 1008,
        text: 'Reminder. Two tracks, knees and toes match, chin level.',
      },

      // 39:00-42:00 Intermediate combo phrase (center)
      {
        time: 1020,
        text: 'Combo teach. First: tendu front right, close, tendu side right, close, demi plie first, stretch.',
      },
      {
        time: 1032,
        text: 'Then: chasse right, pas de bourree right, balance left. Arms flow: low, first, second, down.',
      },
      { time: 1044, text: 'Run combo to the right once. Small and clean.' },
      {
        time: 1056,
        text: 'Mirror to the left: tendu front left, side left, plie, chasse left, pas de bourree left, balance right.',
      },
      { time: 1068, text: 'Run combo to the left once. Shoulders down.' },
      {
        time: 1080,
        text: 'Reminder. Hips steady, knees and toes match, fingers soft.',
      },
      { time: 1092, text: 'Combo both sides again with a small smile.' },

      // 42:00-44:00 Performance run and bow prep
      {
        time: 1104,
        text: 'Performance run. Do right side combo, then left side combo without stopping.',
      },
      { time: 1116, text: 'Spot your head on any turns. Keep arms smooth.' },
      {
        time: 1128,
        text: 'Hold first. Releve balance for 3, 2, 1. Lower slow.',
      },
      {
        time: 1140,
        text: 'Reverence prep. Arms open to second, then to low arms. Calm breath.',
      },

      // 44:00-45:00 Cooldown and reverence
      {
        time: 1152,
        text: 'Butterfly stretch. Sit tall, feet together, knees wide, hold ankles. Hold for 3, 2, 1.',
      },
      {
        time: 1164,
        text: 'Seated hamstring fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      { time: 1176, text: 'Roll to stand. First position. Tall spine.' },
      {
        time: 1188,
        text: 'Arms open to second. Breathe in. Arms down to low arms. Breathe out.',
      },
      {
        time: 1200,
        text: 'Reverence definition. A small bow or curtsey to say thank you.',
      },
      { time: 1212, text: 'Do a small reverence. Hold for 3, 2, 1. Smile.' },

      // Calm breath and posture holds to 45:00
      {
        time: 1224,
        text: 'Posture scan. Tall spine, shoulders down, chin level.',
      },
      {
        time: 1236,
        text: 'Turnout reminder. Small from hips, knees and toes match.',
      },
      { time: 1248, text: 'Breathe in for 3, 2, 1.' },
      { time: 1260, text: 'Breathe out for 3, 2, 1.' },
      { time: 1272, text: 'Stand tall in first for 3, 2, 1.' },
      { time: 1284, text: 'Arms low. Fingers soft.' },
      { time: 1296, text: 'Shoulders down. Neck long.' },
      { time: 1308, text: 'You danced with care today.' },
      { time: 1320, text: 'Calm face. Quiet feet.' },
      { time: 1332, text: 'Hold still for 3, 2, 1.' },
      { time: 1344, text: 'Deep breath in. Long breath out.' },
      {
        time: 1356,
        text: 'Session complete. Intermediate Ballet Basics finished. Great work today.',
      },
    ],
    timeTotal: 1360,
    equipment: 'None',
  },
  // ADVANCED BALLET BASICS (45 minutes)
  {
    id: 2192,
    name: 'Advanced Ballet Basics',
    categories: ['Basics', 'advanced'],
    sports: ['ballet'],
    difficulty: 'advanced',
    skill: 'Basics',
    points: 22,
    instructions:
      'Advanced Ballet Basics teaches you fancy ballet moves and tricky coordination. This 22-minute session helps you practice cool ballet moves, fun combinations, and expert coordination exercises.',
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, turnout safety
      {
        time: 0,
        text: 'Stand tall in Mountain. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Posture definition. Tall spine, chest calm, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 24,
        text: 'Turnout safety. Toes turn out a little from your hips like small pizza slices. Knees and toes point the same way.',
      },
      {
        time: 36,
        text: 'Arms ready. Fingers long, wrists soft, elbows rounded. Low arms in a gentle circle.',
      },
      {
        time: 48,
        text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1. Quiet feet.',
      },
      { time: 60, text: 'Ankles roll. Right two, left two. Knees stay soft.' },
      { time: 72, text: 'Neck long. Shoulders down. Small smile.' },
      {
        time: 84,
        text: 'Stand tall. Hold still for 3, 2, 1. Ready for ballet.',
      },

      // 3:00-7:00 Positions and demi plié: first, second, fifth, fourth
      {
        time: 96,
        text: 'First position. Heels together, toes out a little. Knees over toes, spine tall.',
      },
      {
        time: 108,
        text: 'Demi plie in first. Bend small, heels down, stretch tall.',
      },
      { time: 120, text: 'Repeat demi plie in first. Knees track over toes.' },
      {
        time: 132,
        text: 'Second position. Step one foot to the side, feet wide, toes out a little.',
      },
      {
        time: 144,
        text: 'Demi plie in second. Bend small, press knees over toes, stretch tall.',
      },
      {
        time: 156,
        text: 'Repeat demi plie in second. Spine tall, heels down.',
      },
      {
        time: 168,
        text: 'Fifth position. Right foot front, heel to big toe, small turnout. Knees match toes.',
      },
      {
        time: 180,
        text: 'Demi plie in fifth. Bend tiny, hips square, stretch tall. No grand plie in fifth.',
      },
      {
        time: 192,
        text: 'Fourth position. Step front foot forward, feet apart, small turnout, hips square.',
      },
      {
        time: 204,
        text: 'Demi plie in fourth. Bend small, stretch tall. Shoulders down.',
      },
      { time: 216, text: 'Return to first. Breathe steady.' },

      // 7:00-11:00 Port de bras with positions
      {
        time: 228,
        text: 'Arms first. Gentle circle in front. Elbows rounded.',
      },
      {
        time: 240,
        text: 'Arms second. Open wide, elbows soft, palms forward.',
      },
      {
        time: 252,
        text: 'Arms fifth. Lift overhead in a soft circle. Shoulders stay down.',
      },
      {
        time: 264,
        text: 'Port de bras flow. Low arms to first to second to fifth, then down.',
      },
      { time: 276, text: 'Repeat port de bras. Breathe with the arms.' },
      { time: 288, text: 'Reminder. Neck long, shoulders down, fingers soft.' },
      { time: 300, text: 'Hold second for 3, 2, 1. Posture tall.' },
      { time: 312, text: 'Lower to low arms. Quiet feet.' },

      // 11:00-15:00 Tendu en croix with épaulement
      {
        time: 324,
        text: 'Tendu definition. Brush the foot along the floor to a point, heel forward, toes long, then close.',
      },
      {
        time: 336,
        text: 'From fifth. Tendu front right, close to fifth. Then left, close.',
      },
      {
        time: 348,
        text: 'Add épaulement. Turn head and shoulders gently toward the working foot. Shoulders down.',
      },
      { time: 360, text: 'Tendu side right, close. Left, close. Arms second.' },
      {
        time: 372,
        text: 'Tendu back right, close. Left, close. Heel up in back.',
      },
      { time: 384, text: 'Reminder. Knees straight, toes long, hips quiet.' },
      {
        time: 396,
        text: 'En croix right: front, side, back, close. Small and clean.',
      },
      {
        time: 408,
        text: 'En croix left: front, side, back, close. Breath steady.',
      },

      // 15:00-18:00 Dégagé accents and point-flex
      {
        time: 420,
        text: 'Degage definition. Brush the foot off the floor a little, toes point, then close.',
      },
      {
        time: 432,
        text: 'Degage front right two, close. Degage front left two, close.',
      },
      {
        time: 444,
        text: 'Degage side right two, left two. Sharp accent out, close with control.',
      },
      {
        time: 456,
        text: 'Point flex definition. Point toes long, then flex toes up, knee straight.',
      },
      {
        time: 468,
        text: 'Point flex right two, close. Point flex left two, close.',
      },
      { time: 480, text: 'Reminder. Hips steady, knees straight, toes long.' },

      // 18:00-21:00 Rond de jambe à terre both directions
      {
        time: 492,
        text: 'Rond de jambe definition. Draw a circle with your toe on the floor: front to side to back, then close.',
      },
      {
        time: 504,
        text: 'Right leg three circles outward. Front, side, back, close. Hips square.',
      },
      {
        time: 516,
        text: 'Right leg three circles inward. Back, side, front, close. Spine tall.',
      },
      { time: 528, text: 'Left leg three circles outward. Smooth and even.' },
      { time: 540, text: 'Left leg three circles inward. Breathe steady.' },
      { time: 552, text: 'Reminder. Tummy snug, shoulders down, toes long.' },

      // 21:00-24:00 Frappé and petit battement
      {
        time: 564,
        text: 'Frappé definition. Flex foot at ankle, strike the floor to a quick point, then return to the ankle.',
      },
      {
        time: 576,
        text: 'Frappé front right four, then left four. Small and crisp.',
      },
      {
        time: 588,
        text: 'Frappé side right four, then left four. Knees straight.',
      },
      {
        time: 600,
        text: 'Petit battement definition. At the ankle, switch front to back quickly, knees together.',
      },
      {
        time: 612,
        text: 'Petit battement eight counts each leg. Quiet thighs.',
      },
      { time: 624, text: 'Reminder. Hips steady, toes long, spine tall.' },

      // 24:00-27:00 Fondu and développé to 45 or 60
      {
        time: 636,
        text: 'Fondu definition. Bend both knees, then stretch as the working leg lengthens. Smooth and even.',
      },
      {
        time: 648,
        text: 'Fondu front right two, close. Fondu front left two, close.',
      },
      {
        time: 660,
        text: 'Developpe definition. Knee lifts to passé, leg unfolds to about 45 or 60, then close.',
      },
      {
        time: 672,
        text: 'Developpe front right to 45 or 60, close. Then left, close. Hips level.',
      },
      {
        time: 684,
        text: 'Developpe side right small, close. Then left, close. Shoulders down.',
      },
      {
        time: 696,
        text: 'Reminder. Knee opens to the side, toes long, spine tall.',
      },

      // 27:00-30:00 Grand battement safe
      {
        time: 708,
        text: 'Grand battement definition. Big brush and kick with a straight knee to a safe height, then control the close.',
      },
      {
        time: 720,
        text: 'Grand battement front right two, close. Left two, close. Control down.',
      },
      {
        time: 732,
        text: 'Grand battement side right two, close. Left two, close. Hips quiet.',
      },
      {
        time: 744,
        text: 'Grand battement back right two, close. Left two, close. Heel up in back.',
      },
      {
        time: 756,
        text: 'Reminder. Lift up through spine, tummy snug, land toes then heels.',
      },

      // 30:00-33:00 Relevé, passé, arabesque lines
      {
        time: 768,
        text: 'Releve rise and balance in fifth for 3, 2, 1. Lower with control.',
      },
      {
        time: 780,
        text: 'Passe balance right for 3, 2, 1, then left for 3, 2, 1.',
      },
      {
        time: 792,
        text: 'Arabesque line definition. Leg stretches back, spine long, shoulders down, hips square.',
      },
      {
        time: 804,
        text: 'Arabesque line right small hold for 3, 2, 1, then left hold.',
      },
      { time: 816, text: 'Reminder. Neck long, shoulders down, soft fingers.' },

      // 33:00-36:00 Pirouette prep, half turns, spotting
      {
        time: 828,
        text: 'Pirouette prep. From fourth lunge, push to releve in passe, turn a quarter to half, then close.',
      },
      {
        time: 840,
        text: 'Spotting definition. Pick a point with your eyes, turn body, snap your head back to the point.',
      },
      {
        time: 852,
        text: 'Fourth prep right. Push to releve, half turn, close. Spot the point.',
      },
      { time: 864, text: 'Repeat right. Half turn, close. Hips square.' },
      {
        time: 876,
        text: 'Fourth prep left. Push to releve, half turn, close. Spot the point.',
      },
      {
        time: 888,
        text: 'Repeat left. Half turn, close. Knees and toes match.',
      },

      // 36:00-39:00 Allegro: échappé, changement, glissade, assemblé, jeté small
      {
        time: 900,
        text: 'Echappe. From first to second and back. Two tiny jumps. Knees over toes, land soft.',
      },
      {
        time: 912,
        text: 'Changement definition. Small jump, switch feet in fifth, land soft, bend small.',
      },
      { time: 924, text: 'Changement two times. Quiet feet.' },
      {
        time: 936,
        text: 'Glissade definition. Brush and glide to the side, feet close in the air, land in fifth.',
      },
      { time: 948, text: 'Glissade right small, then left small.' },
      {
        time: 960,
        text: 'Assemble definition. Brush one foot, jump and assemble both feet together to land.',
      },
      { time: 972, text: 'Assemble right small, then left small.' },
      {
        time: 984,
        text: 'Jete petit definition. Small brush and jump to land on the other foot, then close.',
      },
      {
        time: 996,
        text: 'Jete right small, jete left small. Land through toes, then heels.',
      },
      { time: 1008, text: 'Reminder. Spine tall, chin level, shoulders down.' },

      // 39:00-42:00 Travel link: chassé, pas de bourrée, balancé, waltz step
      {
        time: 1020,
        text: 'Chasse right, pas de bourree right, balance left. Arms flow low to first to second.',
      },
      {
        time: 1032,
        text: 'Chasse left, pas de bourree left, balance right. Smooth and musical.',
      },
      {
        time: 1044,
        text: 'Waltz step definition. Step, side, close with a light rise. Arms soft and rounded.',
      },
      {
        time: 1056,
        text: 'Waltz right two counts, then left two counts. Quiet feet.',
      },
      {
        time: 1068,
        text: 'Repeat link once more. Two tracks, knees and toes match.',
      },

      // 42:00-44:00 Advanced combo phrase (center)
      {
        time: 1080,
        text: 'Combo teach. Right: tendu front, side, back, close. Fondu front, developpe front to 45, close.',
      },
      {
        time: 1092,
        text: 'Then: glissade right, assemble right, chasse left, pas de bourree left, balance right.',
      },
      { time: 1104, text: 'Run right side combo once. Small and clean.' },
      {
        time: 1116,
        text: 'Mirror left: tendu front, side, back, close. Fondu front, developpe front, close.',
      },
      {
        time: 1128,
        text: 'Then: glissade left, assemble left, chasse right, pas de bourree right, balance left.',
      },
      { time: 1140, text: 'Run left side combo once. Shoulders down.' },
      {
        time: 1152,
        text: 'Reminder. Hips steady, knees and toes match, fingers soft.',
      },

      // 44:00-45:00 Performance run and reverence
      {
        time: 1164,
        text: 'Performance run. Do right side combo, then left side combo without stopping.',
      },
      { time: 1176, text: 'Spot your head on any turns. Keep arms smooth.' },
      {
        time: 1188,
        text: 'Hold first. Releve balance for 3, 2, 1. Lower slow.',
      },
      {
        time: 1200,
        text: 'Reverence definition. A small bow or curtsey to say thank you.',
      },
      { time: 1212, text: 'Do a small reverence. Hold for 3, 2, 1. Smile.' },

      // Calm breath and posture holds to 45:00
      {
        time: 1224,
        text: 'Posture scan. Tall spine, shoulders down, chin level.',
      },
      {
        time: 1236,
        text: 'Turnout reminder. Small from hips, knees and toes match.',
      },
      { time: 1248, text: 'Breathe in for 3, 2, 1.' },
      { time: 1260, text: 'Breathe out for 3, 2, 1.' },
      { time: 1272, text: 'Stand tall in first for 3, 2, 1.' },
      { time: 1284, text: 'Arms low. Fingers soft.' },
      { time: 1296, text: 'Shoulders down. Neck long.' },
      { time: 1308, text: 'You danced with care today.' },
      { time: 1320, text: 'Calm face. Quiet feet.' },
      { time: 1332, text: 'Hold still for 3, 2, 1.' },
      {
        time: 1344,
        text: 'Deep breath in. Long breath out... Session complete. Advanced Ballet Basics finished. Great work today.',
      },
    ],
    timeTotal: 1348,
    equipment: 'None',
  },
  // BEGINNER BALLET MOVEMENT (30 minutes) Done
  {
    id: 2193,
    name: 'Beginner Ballet Movement',
    categories: ['Movement', 'beginner'],
    sports: ['ballet'],
    difficulty: 'beginner',
    skill: 'Movement',
    points: 19,
    instructions:
      'Beginner Ballet Movement teaches you basic ballet steps and coordination. This 19-minute session helps you learn simple ballet movements, improve your coordination, and practice graceful steps.',
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, turnout safety
      {
        time: 0,
        text: 'Stand tall in Mountain. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Posture definition. Tall spine, chest calm, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 24,
        text: 'Turnout safety. Toes turn out a little from your hips like small pizza slices. Knees point the same way as toes.',
      },
      {
        time: 36,
        text: 'Arms ready. Fingers long like you hold bubbles. Wrists soft, elbows rounded.',
      },
      { time: 48, text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1.' },
      { time: 60, text: 'Ankles roll. Right two, left two. Knees stay soft.' },
      { time: 72, text: 'Neck long. Shoulders down. Small smile.' },
      {
        time: 84,
        text: 'Stand tall. Hold still for 3, 2, 1. Ready for ballet.',
      },
      {
        time: 96,
        text: 'Reminder. Turnout is small and safe. Knees and toes match.',
      },
      {
        time: 108,
        text: 'Posture scan. Tall spine, chin level, shoulders down.',
      },

      // 3:00-6:00 Ballet walk basics
      {
        time: 120,
        text: 'Ballet walk definition. Step with toes pointed, roll through the foot, stand tall, arms calm.',
      },
      {
        time: 132,
        text: 'Walk forward 4 small steps in place. Toes point, roll through, tall spine.',
      },
      {
        time: 144,
        text: 'Walk back 4 small steps. Quiet feet. Knees over toes.',
      },
      {
        time: 156,
        text: 'Walk side 4 small steps right. Then left. Arms low.',
      },
      { time: 168, text: 'Hold first position for 3, 2, 1. Shoulders down.' },
      {
        time: 180,
        text: 'Reminder. Two tracks, toes and knees match, head over hips.',
      },

      // 6:00-9:00 Tiptoe walk and prance
      {
        time: 192,
        text: 'Tiptoe walk definition. Releve rise, tiny steps on the balls of your feet, then lower with control.',
      },
      { time: 204, text: 'Rise to releve. Tiptoe 4 tiny steps. Lower slow.' },
      { time: 216, text: 'Repeat tiptoe walk. Arms low, tummy snug.' },
      {
        time: 228,
        text: 'Prance definition. Lift one heel, then the other like a tiny horse step. Toes point down gently.',
      },
      { time: 240, text: 'Prance 8 counts. Tall spine. Quiet feet.' },
      { time: 252, text: 'Hold first for 3, 2, 1. Breathe steady.' },

      // 9:00-12:00 Tendu walk
      {
        time: 264,
        text: 'Tendu definition. Brush the foot along the floor to a point, heel forward, toes long, then close.',
      },
      {
        time: 276,
        text: 'Tendu walk front. Right brush and step, close. Left brush and step, close. 4 counts.',
      },
      {
        time: 288,
        text: 'Tendu side in place. Right brush side, close. Left brush side, close. 4 counts.',
      },
      {
        time: 300,
        text: 'Tendu back in place. Right brush back, close. Left brush back, close. Heel up in back.',
      },
      { time: 312, text: 'Reminder. Hips steady, knees straight, toes long.' },
      { time: 324, text: 'Hold first for 3, 2, 1. Shoulders down.' },

      // 12:00-15:00 Arms while walking
      {
        time: 336,
        text: 'Arms first. Gentle circle in front. Start ballet walk 4 steps.',
      },
      {
        time: 348,
        text: 'Arms second. Open wide. Walk back 4 steps. Quiet feet.',
      },
      {
        time: 360,
        text: 'Arms fifth. Lift overhead. Walk side 4 steps right, then left.',
      },
      {
        time: 372,
        text: 'Port de bras flow. Low arms to first to second to fifth, then down to low.',
      },
      {
        time: 384,
        text: 'Repeat port de bras with still feet. Breathe with the arms.',
      },
      { time: 396, text: 'Reminder. Neck long, shoulders down, fingers soft.' },

      // 15:00-18:00 Patterns: circle, square, zigzag
      {
        time: 408,
        text: 'Circle pattern definition. Walk a tiny circle with small steps, posture tall.',
      },
      { time: 420, text: 'Walk a small circle right 8 counts. Arms low.' },
      { time: 432, text: 'Walk a small circle left 8 counts. Quiet feet.' },
      {
        time: 444,
        text: 'Square pattern. Forward, right, back, left. Small steps.',
      },
      {
        time: 456,
        text: 'Walk a small square 8 counts. Two tracks, knees over toes.',
      },
      {
        time: 468,
        text: 'Zigzag pattern. Two small diagonal steps right, then two left.',
      },
      { time: 480, text: 'Do one zigzag set. Smile. Stand tall for 3, 2, 1.' },

      // 18:00-21:00 Chassé and gallop
      {
        time: 492,
        text: 'Chasse definition. Step, close, step. Feet kiss in the middle. Glide like a slide.',
      },
      { time: 504, text: 'Chasse right small, then left small. Arms low.' },
      { time: 516, text: 'Repeat chasses. Land soft.' },
      {
        time: 528,
        text: 'Gallop definition. Lead foot steps, back foot catches up, small and light.',
      },
      {
        time: 540,
        text: 'Gallop right small 4 counts. Then left small 4 counts.',
      },
      { time: 552, text: 'Reminder. Two tracks, heels light, chest calm.' },

      // 21:00-24:00 Pas de bourrée and balancé
      {
        time: 564,
        text: 'Pas de bourree definition. Back, side, front steps. Tiny triangle. Light and quick.',
      },
      { time: 576, text: 'Pas de bourree right one time. Then left one time.' },
      { time: 588, text: 'Repeat pas de bourree right and left. Arms low.' },
      {
        time: 600,
        text: 'Balance step definition. Side, back, front rocking step with arms open and soft.',
      },
      {
        time: 612,
        text: 'Balance right once, then left once. Smooth and musical.',
      },
      { time: 624, text: 'Reminder. Knees and toes match. Shoulders down.' },

      // 24:00-27:00 Sauté small and échappé prep
      {
        time: 636,
        text: 'Saute definition. Tiny straight jump in place. Heels together in first, toes out, land soft, knees over toes.',
      },
      {
        time: 648,
        text: 'Do four small sautes in first. Land quiet, bend small.',
      },
      { time: 660, text: 'Hold first. Breathe in, breathe out.' },
      {
        time: 672,
        text: 'Echappe prep definition. From first, jump to second and back to first, tiny and soft.',
      },
      {
        time: 684,
        text: 'Echappe prep. Tiny jump to second, land soft, then first, land soft.',
      },
      {
        time: 696,
        text: 'Repeat echappe prep one more time. Knees over toes.',
      },
      {
        time: 708,
        text: 'Low impact option. Rise to releve and open to second, then close to first, without a jump.',
      },

      // 27:00-29:00 Mini dance link
      {
        time: 720,
        text: 'Mini dance. Tendu walk 2 steps, arms to first. Chasse right, chasse left. Gallop right, gallop left.',
      },
      {
        time: 732,
        text: 'Add. Pas de bourree right, pas de bourree left. Balance right, balance left.',
      },
      { time: 744, text: 'Repeat mini dance from the top. Small and clean.' },
      {
        time: 756,
        text: 'Reminder. Knees and toes match. Shoulders down. Fingers soft.',
      },
      {
        time: 768,
        text: 'Balance in first on releve for 3, 2, 1. Lower with control.',
      },
      { time: 780, text: 'Stand tall. Breathe steady.' },

      // 29:00-30:00 Cooldown and reverence
      {
        time: 792,
        text: 'Butterfly stretch. Sit tall, feet together, knees wide, hold ankles. Hold for 3, 2, 1.',
      },
      {
        time: 804,
        text: 'Seated hamstring fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      { time: 816, text: 'Roll to stand. First position. Tall spine.' },
      {
        time: 828,
        text: 'Arms open to second. Breathe in. Arms down to low arms. Breathe out.',
      },
      {
        time: 840,
        text: 'Reverence definition. A small bow or curtsey to say thank you.',
      },
      { time: 852, text: 'Do a small reverence. Hold for 3, 2, 1. Smile.' },
      {
        time: 864,
        text: 'Posture scan. Tall spine, shoulders down, chin level.',
      },
      { time: 876, text: 'Thank your body for dancing. Breathe in.' },
      { time: 888, text: 'Breathe out. Quiet feet.' },
      { time: 900, text: 'Stand tall in first for 3, 2, 1.' },
      { time: 912, text: 'Calm breath in.' },
      { time: 924, text: 'Calm breath out.' },
      { time: 936, text: 'Hands to low arms. Shoulders soft.' },
      { time: 948, text: 'Small smile. Eyes forward.' },
      { time: 960, text: 'Two tracks, knees and toes match.' },
      { time: 972, text: 'Neck long, tummy snug.' },
      { time: 984, text: 'Quiet body for 3, 2, 1.' },
      { time: 996, text: 'You danced with care today.' },
      { time: 1008, text: 'Stand proud. Breathe steady.' },
      { time: 1020, text: 'Arms open to second, then down.' },
      { time: 1032, text: 'Thank you, dancer. Hold still for 3, 2, 1.' },
      { time: 1044, text: 'Deep breath in.' },
      { time: 1056, text: 'Long breath out.' },
      { time: 1068, text: 'Hands by sides. Quiet feet.' },
      { time: 1080, text: 'Head over hips. Tall spine.' },
      { time: 1092, text: 'Calm face. Soft eyes.' },
      { time: 1104, text: 'Stand in first. Hold for 3, 2, 1.' },
      { time: 1116, text: 'Small smile. Breathe in.' },
      {
        time: 1128,
        text: 'Breathe out. Ballet movement complete. Great work today.',
      },
    ],
    timeTotal: 1132,
    equipment: 'None',
  },
  // INTERMEDIATE BALLET MOVEMENT (45 minutes)
  {
    id: 2194,
    name: 'Intermediate Ballet Movement',
    categories: ['Movement', 'intermediate'],
    sports: ['ballet'],
    difficulty: 'intermediate',
    skill: 'Movement',
    points: 40,
    instructions:
      'Intermediate Ballet Movement builds on your basic ballet steps with harder moves and better coordination. This 40-minute session helps you practice better ballet movements, fun combinations, and cool patterns.',
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, turnout safety
      {
        time: 0,
        text: 'Stand tall in Mountain. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Posture. Tall spine, chest calm, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 48,
        text: 'Turnout safety. Toes turn out a little from your hips. Knees point the same way as toes.',
      },
      {
        time: 72,
        text: 'Arms ready. Fingers long like you hold bubbles. Wrists soft, elbows rounded.',
      },
      {
        time: 96,
        text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1. Quiet feet.',
      },
      { time: 120, text: 'Ankles roll. Right two, left two. Knees stay soft.' },
      {
        time: 144,
        text: 'Stand tall. Hold still for 3, 2, 1. Turnout is small and safe.',
      },

      // 3:00-6:00 Ballet walk with arms
      {
        time: 168,
        text: 'Ballet walk. Step with toes pointed, roll through the foot, stand tall.',
      },
      { time: 192, text: 'Walk forward 4 small steps. Arms low. Quiet feet.' },
      { time: 216, text: 'Walk back 4 small steps. Knees over toes.' },
      {
        time: 240,
        text: 'Arms first. Gentle circle in front. Walk side 4 steps right, then left.',
      },
      {
        time: 264,
        text: 'Arms second. Open wide. Walk forward 4 steps. Shoulders down.',
      },
      {
        time: 288,
        text: 'Arms fifth. Lift overhead. Walk back 4 steps. Neck long.',
      },

      // 6:00-9:00 Tiptoe walk, prance, tendu walk
      {
        time: 312,
        text: 'Tiptoe walk. Rise to releve, tiny steps on the balls of your feet, lower with control.',
      },
      { time: 336, text: 'Tiptoe 6 counts. Lower slow. Arms low.' },
      {
        time: 360,
        text: 'Prance. Lift one heel then the other. Toes point down gently.',
      },
      { time: 384, text: 'Prance 8 counts. Tall spine. Quiet feet.' },
      {
        time: 408,
        text: 'Tendu walk. Brush to a point, step, close. Hips steady.',
      },
      {
        time: 432,
        text: 'Tendu walk front 4 counts. Knees straight, toes long.',
      },

      // 9:00-12:00 Tendu en croix in place
      { time: 456, text: 'From first. Tendu front right, close. Left, close.' },
      {
        time: 480,
        text: 'Tendu side right, close. Left, close. Heel forward.',
      },
      {
        time: 504,
        text: 'Tendu back right, close. Left, close. Heel up in back.',
      },
      { time: 528, text: 'Reminder. Hips quiet, knees straight, toes long.' },
      { time: 552, text: 'En croix right: front, side, back, close.' },
      {
        time: 576,
        text: 'En croix left: front, side, back, close. Breathe steady.',
      },

      // 12:00-15:00 Chassé and gallop
      {
        time: 600,
        text: 'Chasse. Step, close, step. Feet kiss in the middle. Glide like a slide.',
      },
      { time: 624, text: 'Chasse right small, then left small. Land soft.' },
      {
        time: 648,
        text: 'Chasse right, freeze first for 3, 2, 1. Then left, freeze.',
      },
      {
        time: 672,
        text: 'Gallop. Lead foot steps, back foot catches up. Small and light.',
      },
      {
        time: 696,
        text: 'Gallop right 6 counts, then left 6 counts. Chest calm.',
      },
      { time: 720, text: 'Reminder. Two tracks, heels light, shoulders down.' },

      // 15:00-18:00 Waltz step and polonaise feel (safe, small)
      {
        time: 744,
        text: 'Waltz step. Step, side, close with a tiny rise. Arms rounded and soft.',
      },
      { time: 768, text: 'Waltz right 2 bars, then left 2 bars. Quiet feet.' },
      { time: 792, text: 'Waltz walking circle 8 counts. Posture tall.' },
      {
        time: 816,
        text: 'Waltz change of side. Turn your body a little as you step. Head over hips.',
      },
      { time: 840, text: 'Reminder. Knees and toes match. Fingers soft.' },
      { time: 864, text: 'Hold first. Breathe in, breathe out.' },

      // 18:00-21:00 Pas de bourrée and balancé
      {
        time: 888,
        text: 'Pas de bourree. Back, side, front. Tiny triangle. Light and quick.',
      },
      { time: 912, text: 'Pas de bourree right, then left. Arms low.' },
      { time: 936, text: 'Repeat pas de bourree right and left. Smooth feet.' },
      {
        time: 960,
        text: 'Balance step. Side, back, front rocking step with arms open.',
      },
      {
        time: 984,
        text: 'Balance right once, then left once. Musical and soft.',
      },
      { time: 1008, text: 'Reminder. Two tracks, chin level, shoulders down.' },

      // 21:00-24:00 Glissade and assemblé small
      {
        time: 1032,
        text: 'Glissade. Brush and glide to the side, feet close in the air, land in fifth.',
      },
      {
        time: 1056,
        text: 'Glissade right small, then left small. Knees over toes.',
      },
      {
        time: 1080,
        text: 'Assemble. Brush one foot, jump and assemble both feet together to land soft.',
      },
      {
        time: 1104,
        text: 'Assemble right small, then left small. Land through toes, then heels.',
      },
      { time: 1128, text: 'Reminder. Spine tall, tummy snug, quiet feet.' },
      { time: 1152, text: 'Hold first for 3, 2, 1. Breathe steady.' },

      // 24:00-27:00 Piqué walks and soutenu half turns
      {
        time: 1176,
        text: 'Pique walk. Step onto a straight leg with a pointed foot, other leg in coupe or low passé.',
      },
      {
        time: 1200,
        text: 'Pique 6 counts forward on a tiny line. Arms in first.',
      },
      {
        time: 1224,
        text: 'Soutenu half turn. Close feet, turn around on both feet, spot with your eyes.',
      },
      {
        time: 1248,
        text: 'Do 2 soutenu half turns. Shoulders down, head spots.',
      },
      { time: 1272, text: 'Repeat. Pique 6 counts, soutenu half turn once.' },
      {
        time: 1296,
        text: 'Reminder. Hips steady, knees and toes match, neck long.',
      },

      // 27:00-30:00 Tendu travel phrase
      {
        time: 1320,
        text: 'Phrase teach. Tendu travel: tendu front and step, tendu side and close, pas de bourree, balance.',
      },
      {
        time: 1344,
        text: 'Run phrase to the right one time. Arms low to first to second.',
      },
      {
        time: 1368,
        text: 'Mirror phrase to the left one time. Small and clean.',
      },
      { time: 1392, text: 'Repeat both sides. Quiet feet, smooth arms.' },
      {
        time: 1416,
        text: 'Reminder. Knees and toes match, turnout small and safe.',
      },
      { time: 1440, text: 'Hold first. Breathe steady. Shoulders down.' },

      // 30:00-33:00 Allegro link: chassé, glissade, assemblé, jeté petit
      {
        time: 1464,
        text: 'Allegro link. Chasse right, glissade right, assemble right, jete petit right, then close.',
      },
      {
        time: 1488,
        text: 'Do the link to the right once. Land soft, bend small.',
      },
      { time: 1512, text: 'Mirror to the left once. Arms stable and soft.' },
      { time: 1536, text: 'Repeat both sides one more time. Quiet feet.' },
      {
        time: 1560,
        text: 'Reminder. Land through toes, then heels. Spine tall.',
      },
      { time: 1584, text: 'Hold first for 3, 2, 1. Calm breath.' },

      // 33:00-36:00 Port de bras with side bend and arabesque line
      {
        time: 1608,
        text: 'Port de bras. Low arms to first to second to fifth, then down.',
      },
      {
        time: 1632,
        text: 'Side bend right small with arms in second. Then left small.',
      },
      {
        time: 1656,
        text: 'Arabesque line. Leg back small, hips square, shoulders down, arm forward soft.',
      },
      {
        time: 1680,
        text: 'Arabesque right hold 3, 2, 1. Lower. Arabesque left hold 3, 2, 1. Lower.',
      },
      { time: 1704, text: 'Reminder. Neck long, tummy snug, fingers soft.' },
      { time: 1728, text: 'Releve balance in first 3, 2, 1. Lower slow.' },

      // 36:00-39:00 Spotting practice and chain prep
      {
        time: 1752,
        text: 'Spotting. Pick a point with your eyes. Turn your body a little and snap your head back to the point.',
      },
      { time: 1776, text: 'Quarter turns right two times. Spot the point.' },
      { time: 1800, text: 'Quarter turns left two times. Spot the point.' },
      {
        time: 1824,
        text: 'Chain prep. Tiny walks in a circle with quick spot. Arms low.',
      },
      {
        time: 1848,
        text: 'Do tiny circle right 6 counts, then left 6 counts. Posture tall.',
      },
      {
        time: 1872,
        text: 'Reminder. Head over hips, chin level, shoulders down.',
      },

      // 39:00-42:00 Intermediate center combo
      {
        time: 1896,
        text: 'Combo teach. Right: waltz step 2, pas de bourree right, balance left, soutenu half turn.',
      },
      {
        time: 1920,
        text: 'Add: glissade right, assemble right, tendu front close, arms flow low to first to second.',
      },
      { time: 1944, text: 'Run combo to the right once. Small and clean.' },
      {
        time: 1968,
        text: 'Mirror left: waltz step 2, pas de bourree left, balance right, soutenu half turn.',
      },
      {
        time: 1992,
        text: 'Add: glissade left, assemble left, tendu front close, arms soft.',
      },
      { time: 2016, text: 'Run combo to the left once. Breathe steady.' },

      // 42:00-44:00 Performance run
      {
        time: 2040,
        text: 'Performance run. Do right combo then left combo without stopping.',
      },
      {
        time: 2064,
        text: 'Spot your head on turns. Keep arms smooth and rounded.',
      },
      {
        time: 2088,
        text: 'Hold first. Releve balance for 3, 2, 1. Lower slow.',
      },
      {
        time: 2112,
        text: 'Reminder. Turnout small and safe. Knees and toes match.',
      },

      // 44:00-45:00 Cooldown and reverence
      {
        time: 2136,
        text: 'Butterfly stretch. Sit tall, feet together, knees wide, hold ankles. Hold for 3, 2, 1.',
      },
      {
        time: 2160,
        text: 'Seated hamstring fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      { time: 2184, text: 'Roll to stand. First position. Tall spine.' },
      {
        time: 2208,
        text: 'Arms open to second. Breathe in. Arms down to low arms. Breathe out.',
      },
      {
        time: 2232,
        text: 'Reverence. A small bow or curtsey to say thank you. Hold for 3, 2, 1.',
      },
      {
        time: 2256,
        text: 'Posture scan. Tall spine, shoulders down, chin level.',
      },
      { time: 2280, text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1.' },
      { time: 2304, text: 'Hands to low arms. Fingers soft. Quiet feet.' },
      { time: 2328, text: 'You danced with care today. Small smile.' },
      { time: 2352, text: 'Stand tall in first for 3, 2, 1. Shoulders down.' },
      { time: 2376, text: 'Deep breath in. Long breath out.' },
      {
        time: 2400,
        text: 'Session complete. Intermediate Ballet Movement finished. Great work today!',
      },
    ],
    timeTotal: 2404,
    equipment: 'None',
  },
  // ADVANCED BALLET MOVEMENT (45 minutes)
  {
    id: 2195,
    name: 'Advanced Ballet Movement',
    categories: ['Movement', 'advanced'],
    sports: ['ballet'],
    difficulty: 'advanced',
    skill: 'Movement',
    points: 42,
    instructions:
      'Advanced Ballet Movement teaches you fancy ballet steps and tricky coordination. This 42-minute session helps you practice cool ballet movements, fun combinations, and expert coordination exercises.',
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, turnout safety, plies
      {
        time: 0,
        text: 'Stand tall in first. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Posture check. Tall spine, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 48,
        text: 'Turnout safety. Toes turn out from your hips. Knees point the same way as toes. Never twist your knees.',
      },
      {
        time: 72,
        text: 'Arms low. Fingers long like you hold bubbles. Wrists soft, elbows rounded.',
      },
      {
        time: 96,
        text: 'Demi plie in first. Bend both knees small, heels stay down, then stretch tall.',
      },
      {
        time: 120,
        text: 'Two demi plies in first. Knees track over toes. Tall spine.',
      },
      {
        time: 144,
        text: 'Grand plie in first. Bend deeper, heels stay down, then lift a little, then stretch. Chest calm.',
      },
      {
        time: 168,
        text: 'Open to fifth. Heels kiss toes. Two demi plies in fifth. Hips quiet.',
      },
      {
        time: 192,
        text: 'Grand plie in fifth small. Heels peek, then press down. Stand tall.',
      },
      {
        time: 216,
        text: 'Reminder. Knees and toes match. Head over hips. Quiet feet.',
      },

      // 3:00-6:00 Tendu and degage with control
      {
        time: 240,
        text: 'Tendu means to brush to a point. From fifth, brush front, toes long, close to fifth.',
      },
      {
        time: 264,
        text: 'Tendu front right and close. Left and close. Knees straight.',
      },
      {
        time: 288,
        text: 'Tendu side right and close. Left and close. Heel forward.',
      },
      {
        time: 312,
        text: 'Tendu back right and close. Left and close. Heel up in back.',
      },
      {
        time: 336,
        text: 'Degage is a quick brush that leaves the floor. Small lift and close.',
      },
      {
        time: 360,
        text: 'Degage front 4 small. Then side 4 small. Point and close.',
      },
      { time: 384, text: 'Degage back 4 small. Hips steady. Shoulders down.' },

      // 6:00-9:00 Rond de jambe a terre, en dehors and en dedans
      {
        time: 408,
        text: 'Rond de jambe a terre. Draw a half circle with your toes on the floor.',
      },
      { time: 432, text: 'En dehors means out. Front to side to back, close.' },
      {
        time: 456,
        text: 'Right leg en dehors 4 slow. Knees straight. Toes long.',
      },
      { time: 480, text: 'En dedans means in. Back to side to front, close.' },
      { time: 504, text: 'Right leg en dedans 4 slow. Hips quiet.' },
      { time: 528, text: 'Left leg en dehors 4 slow. Breathe steady.' },
      { time: 552, text: 'Left leg en dedans 4 slow. Spine tall, tummy snug.' },

      // 9:00-12:00 Developpe front, side, back
      {
        time: 576,
        text: 'Developpe means to unfold. Lift knee to passe, then stretch the leg, then close.',
      },
      {
        time: 600,
        text: 'Developpe front right slow. Passe, stretch, close. Hips square.',
      },
      {
        time: 624,
        text: 'Developpe side right slow. Toes lead, knee up, close.',
      },
      {
        time: 648,
        text: 'Developpe back right slow. Heel up in back, chest calm, close.',
      },
      {
        time: 672,
        text: 'Left leg. Developpe front, then side, then back, all slow and smooth.',
      },
      {
        time: 696,
        text: 'Reminder. Knees and toes match. Neck long. Shoulders down.',
      },

      // 12:00-15:00 Grand battement with control
      {
        time: 720,
        text: 'Grand battement is a big brush kick. Brush up with a straight knee, control down, and close.',
      },
      {
        time: 744,
        text: 'Grand battement front right 4 controlled. Quiet hips.',
      },
      {
        time: 768,
        text: 'Grand battement side right 4 controlled. Heel leads.',
      },
      {
        time: 792,
        text: 'Grand battement back right 4 controlled. Heel up in back.',
      },
      {
        time: 816,
        text: 'Left leg. Front 4, side 4, back 4. Control the return.',
      },
      {
        time: 840,
        text: 'Reminder. Tall spine. Breathe out on the brush. Land soft.',
      },

      // 15:00-18:00 Adagio center: arabesque line and attitude
      {
        time: 864,
        text: 'Center. Arabesque line. Back leg long, hips square, chest calm, arm forward soft.',
      },
      {
        time: 888,
        text: 'Arabesque right. Hold for 3, 2, 1. Lower with control.',
      },
      {
        time: 912,
        text: 'Arabesque left. Hold for 3, 2, 1. Lower with control.',
      },
      {
        time: 936,
        text: 'Attitude means a bent knee shape. Lift to front attitude small. Hips steady.',
      },
      {
        time: 960,
        text: 'Attitude front right hold 3, 2, 1. Lower. Then left hold 3, 2, 1.',
      },
      {
        time: 984,
        text: 'Attitude back small both sides. Shoulders down, chin level.',
      },

      // 18:00-21:00 Pirouette prep and half turns with spot
      {
        time: 1008,
        text: 'Passe means toe at your knee. Turnout small and safe.',
      },
      {
        time: 1032,
        text: 'Spotting. Pick a point with your eyes. Turn your body and snap your head back to the point.',
      },
      {
        time: 1056,
        text: 'Pirouette prep. From fourth, plie, push to releve, knee to passe, lower and close.',
      },
      {
        time: 1080,
        text: 'Half turn en dehors right two times. Spot each time. Land soft in fourth.',
      },
      {
        time: 1104,
        text: 'Half turn en dehors left two times. Keep spine tall.',
      },
      {
        time: 1128,
        text: 'Reminder. Hips steady. Arms round to first. Breathe steady.',
      },

      // 21:00-24:00 Single pirouettes, clean finish
      {
        time: 1152,
        text: 'Single pirouette en dehors right. Plie, push, spot, finish in fourth.',
      },
      {
        time: 1176,
        text: 'Do two singles to the right. Quiet feet. Control the finish.',
      },
      {
        time: 1200,
        text: 'Single pirouette en dehors left. Plie, push, spot, finish clean.',
      },
      {
        time: 1224,
        text: 'Do two singles to the left. Neck long. Shoulders down.',
      },
      {
        time: 1248,
        text: 'Balance in passe right for 3, 2, 1. Lower. Then left for 3, 2, 1.',
      },
      {
        time: 1272,
        text: 'Reminder. Knees over toes in plie. Head over hips.',
      },

      // 24:00-27:00 Chaines and pique turns small
      {
        time: 1296,
        text: 'Chaines are tiny quick turns on two feet. Step close, turn, spot, repeat.',
      },
      {
        time: 1320,
        text: 'Chaines across small. Right 6 counts. Spot your point.',
      },
      { time: 1344, text: 'Chaines back small. Left 6 counts. Arms in first.' },
      {
        time: 1368,
        text: 'Pique turn. Step on a straight leg to releve, other leg to low passe, spot, close.',
      },
      { time: 1392, text: 'Pique turns right 4 small. Control the close.' },
      { time: 1416, text: 'Pique turns left 4 small. Breathe steady.' },

      // 27:00-30:00 Allegro link: glissade, assemble, jete petit, sissonne
      {
        time: 1440,
        text: 'Glissade. Brush and glide to the side, feet close in the air, land in fifth.',
      },
      {
        time: 1464,
        text: 'Assemble. Brush one foot, jump and assemble both feet to land soft.',
      },
      {
        time: 1488,
        text: 'Jete petit. Small brush and hop to the other foot. Land soft, bend small.',
      },
      {
        time: 1512,
        text: 'Sissonne. Jump from two feet to one foot, open like scissors, then close.',
      },
      {
        time: 1536,
        text: 'Link right: glissade, assemble, jete petit, sissonne. Then close.',
      },
      {
        time: 1560,
        text: 'Link left: glissade, assemble, jete petit, sissonne. Quiet feet.',
      },

      // 30:00-33:00 Petit allegro: echappe, changement, pas de chat
      {
        time: 1584,
        text: 'Echappe saute. Jump from closed to open, heels press, then close.',
      },
      {
        time: 1608,
        text: 'Do 2 echappe, then 2 changement. Land through toes then heels.',
      },
      {
        time: 1632,
        text: 'Changement means change your feet in the air. Knees point with toes.',
      },
      {
        time: 1656,
        text: 'Pas de chat. Step, lift one knee then the other like a cat, land soft and close.',
      },
      {
        time: 1680,
        text: 'Petit allegro set. Echappe 2, changement 2, pas de chat right and left.',
      },
      {
        time: 1704,
        text: 'Reminder. Bend small on landings. Spine tall. Quiet feet.',
      },

      // 33:00-36:00 Waltz travel and balancé with direction change
      {
        time: 1728,
        text: 'Waltz travel. Step, side, close with a tiny rise. Arms round and soft.',
      },
      {
        time: 1752,
        text: 'Waltz right 2 bars, then left 2 bars. Breathe with the steps.',
      },
      {
        time: 1776,
        text: 'Balance step. Rock side, back, front with open arms.',
      },
      {
        time: 1800,
        text: 'Balance right, then left. Turn your body a little as you go.',
      },
      {
        time: 1824,
        text: 'Waltz turn small. Spot gently. Keep head over hips.',
      },
      { time: 1848, text: 'Reminder. Knees and toes match. Shoulders down.' },

      // 36:00-39:00 Advanced center combo teach
      {
        time: 1872,
        text: 'Combo teach right. Tendu front close, pas de bourree, developpe side, soutenu half turn.',
      },
      {
        time: 1896,
        text: 'Add. Glissade right, assemble right, single pirouette right, finish in fourth.',
      },
      {
        time: 1920,
        text: 'Run right combo once. Small and clean. Spot the turn.',
      },
      {
        time: 1944,
        text: 'Combo teach left. Tendu front close, pas de bourree, developpe side, soutenu half turn.',
      },
      {
        time: 1968,
        text: 'Add. Glissade left, assemble left, single pirouette left, finish clean.',
      },
      { time: 1992, text: 'Run left combo once. Quiet feet. Tall spine.' },

      // 39:00-42:00 Grand allegro prep phrase small and safe
      {
        time: 2016,
        text: 'Tombe means to fall into a lunge. Tombe right, pas de bourree, glissade, grand jete prep small.',
      },
      {
        time: 2040,
        text: 'Grand jete prep. Brush front, jump forward small, land soft, close to fifth.',
      },
      {
        time: 2064,
        text: 'Run the prep phrase to the right once. Knees over toes on land.',
      },
      { time: 2088, text: 'Mirror left side once. Chest calm. Arms soft.' },
      {
        time: 2112,
        text: 'Reminder. Land through toes then heels. Breathe steady.',
      },
      {
        time: 2136,
        text: 'Hold first. Releve balance for 3, 2, 1. Lower slow.',
      },

      // 42:00-44:00 Performance run both sides
      {
        time: 2160,
        text: 'Performance run. Right advanced combo, then left advanced combo without stopping.',
      },
      {
        time: 2184,
        text: 'Add the grand allegro prep phrase on your best side. Control the landings.',
      },
      {
        time: 2208,
        text: 'Spot your head on all turns. Arms round and smooth.',
      },
      {
        time: 2232,
        text: 'Finish in first. Breathe in for 3, 2, 1. Breathe out for 3, 2, 1.',
      },

      // 44:00-45:00 Cooldown and reverence
      {
        time: 2256,
        text: 'Butterfly stretch. Sit tall, feet together, knees wide, hold ankles. Hold for 3, 2, 1.',
      },
      {
        time: 2280,
        text: 'Seated hamstring fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      {
        time: 2304,
        text: 'Figure four stretch right small. Ankle over knee, sit tall. Hold for 3, 2, 1.',
      },
      {
        time: 2328,
        text: 'Figure four stretch left small. Breathe easy. Hold for 3, 2, 1.',
      },
      { time: 2352, text: 'Roll to stand. First position. Tall spine.' },
      {
        time: 2376,
        text: 'Port de bras. Low arms to first to second to fifth, then down.',
      },
      {
        time: 2400,
        text: 'Reverence. A small bow or curtsey to say thank you. Hold for 3, 2, 1.',
      },
      {
        time: 2424,
        text: 'Posture scan. Head over hips, shoulders down, chin level.',
      },
      {
        time: 2448,
        text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1. Quiet feet.',
      },
      { time: 2472, text: 'You danced with skill and care. Small smile.' },
      { time: 2496, text: 'Stand tall in first for 3, 2, 1. Arms low.' },
      { time: 2520, text: 'Deep breath in. Long breath out. Calm body.' },
      {
        time: 2524,
        text: 'Session complete. Advanced Ballet Movement finished. Great work today.',
      },
    ],
    timeTotal: 2548,
    equipment: 'None',
  },
  // BEGINNER BALLET GRACE (30 minutes)
  {
    id: 2196,
    name: 'Beginner Ballet Grace',
    categories: ['Grace', 'beginner'],
    sports: ['ballet'],
    difficulty: 'beginner',
    skill: 'Grace',
    points: 25,
    instructions:
      'Beginner Ballet Grace teaches you basic graceful movements and flow. This 25-minute session helps you learn simple graceful moves, improve your flow, and practice beautiful ballet movements.',
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, breathing, turnout safety
      {
        time: 0,
        text: 'Stand tall in first. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 15,
        text: 'Posture check. Tall spine, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 30,
        text: 'Turnout safety. Toes turn out a little from your hips. Knees point the same way as toes.',
      },
      {
        time: 45,
        text: 'Arms low. Fingers long like you hold bubbles. Wrists soft, elbows rounded.',
      },
      {
        time: 60,
        text: 'Breathe. In for 3, 2, 1. Out for 3, 2, 1. Quiet feet.',
      },
      {
        time: 75,
        text: 'Reminder. Two tracks under your feet. Knees and toes match.',
      },
      {
        time: 90,
        text: 'Neck long. Crown of your head lifts to the sky. Chest calm.',
      },
      {
        time: 105,
        text: 'Small nod yes, small look side to side. Head over hips. Shoulders stay down.',
      },
      {
        time: 120,
        text: 'Arms float to first. Round like a hug in front of your belly. Fingers soft.',
      },
      {
        time: 135,
        text: 'Arms open to second. Wide like wings. Elbows lifted, wrists soft.',
      },
      {
        time: 150,
        text: 'Arms to fifth. Up like a rainbow. Shoulders stay down. Then lower to low arms.',
      },
      {
        time: 165,
        text: 'Breath and balance. Stand still for 3, 2, 1. Quiet toes.',
      },

      // 3:00-6:00 Port de bras basics with grace
      {
        time: 180,
        text: 'Port de bras means carry the arms. Move slow and smooth like water.',
      },
      {
        time: 195,
        text: 'Low arms to first. Pause tall. Open to second. Pause tall. Lower to low arms.',
      },
      {
        time: 210,
        text: 'Add breath. Breathe in as arms rise. Breathe out as arms float down.',
      },
      {
        time: 225,
        text: 'Repeat. First to second to fifth to second to low. Elbows soft, fingers long.',
      },
      {
        time: 240,
        text: 'Side bend right small with arms in second. Tall spine. Come up slow.',
      },
      {
        time: 255,
        text: 'Side bend left small. Keep hips level. Come up slow.',
      },
      {
        time: 270,
        text: 'Arms float, head follows the hand gently. Eyes smile.',
      },
      { time: 285, text: 'Reminder. Two tracks, knees soft, shoulders down.' },
      {
        time: 300,
        text: 'Hold first position. Breathe in for 3, 2, 1. Out for 3, 2, 1.',
      },
      {
        time: 315,
        text: 'Repeat the port de bras once more. Smooth like a wave.',
      },

      // 6:00-9:00 Graceful head and body lines
      {
        time: 330,
        text: 'Head and eyes. Look right with soft eyes. Back to center. Then left. Back to center.',
      },
      {
        time: 345,
        text: 'Head tilt. Ear to shoulder small. Lift back up tall. Other side small. Lift up tall.',
      },
      {
        time: 360,
        text: 'Body sway. Arms in second. Sway right and left like a tree in wind. Knees soft.',
      },
      {
        time: 375,
        text: 'Repeat sway with breath. In on the way up, out on the way over.',
      },
      {
        time: 390,
        text: 'Diagonal reach. Right arm floats up and out. Side bend tiny. Return smooth.',
      },
      {
        time: 405,
        text: 'Diagonal reach other side. Keep neck long, tummy snug.',
      },
      {
        time: 420,
        text: 'Reminder. Tall spine, knees and toes match, quiet feet.',
      },
      {
        time: 435,
        text: 'Freeze in first. Hold still for 3, 2, 1. Soft smile.',
      },

      // 9:00-12:00 Graceful plié with arms
      {
        time: 450,
        text: 'Plie means bend. In first, bend knees small, heels stay down, then stretch tall.',
      },
      {
        time: 465,
        text: 'Add arms. Plie with arms to first, stretch and open to second.',
      },
      {
        time: 480,
        text: 'Two slow plies in first. Knees over toes. Head over hips.',
      },
      {
        time: 495,
        text: 'Grand plie is deeper. Bend a little more, heels stay down, then lift a little and stretch.',
      },
      {
        time: 510,
        text: 'Do one grand plie small and safe. Shoulder down, chest calm.',
      },
      {
        time: 525,
        text: 'Open to second position. Feet wider, toes out, two tracks stay.',
      },
      {
        time: 540,
        text: 'Demi plie in second two times. Knees track with toes. Spine tall.',
      },
      {
        time: 555,
        text: 'Small grand plie in second. Heels stay down. Breathe out as you rise.',
      },
      {
        time: 570,
        text: 'Reminder. Two tracks, knees and toes match, quiet feet.',
      },
      { time: 585, text: 'Arms float down. Stand tall and still for 3, 2, 1.' },

      // 12:00-15:00 Tendu for grace, slow and smooth
      {
        time: 600,
        text: 'Tendu means to brush to a point. Brush your toes out, keep knee straight, then close.',
      },
      {
        time: 615,
        text: 'From first. Tendu front right, close. Tendu front left, close. Toes long.',
      },
      { time: 630, text: 'Arms low. Keep hips quiet and steady.' },
      {
        time: 645,
        text: 'Tendu side right, close. Tendu side left, close. Heel forward.',
      },
      {
        time: 660,
        text: 'Tendu back right, close. Tendu back left, close. Heel up in back.',
      },
      {
        time: 675,
        text: 'Add arms to first, then open to second on the side tendu. Smooth and slow.',
      },
      { time: 690, text: 'Reminder. Knees straight, toes long, tummy snug.' },
      { time: 705, text: 'Hold first. Breathe steady. Shoulders down.' },

      // 15:00-18:00 Ballet walk and tiptoe walk with arms
      {
        time: 720,
        text: 'Ballet walk. Step with toes pointed, roll through your foot, stand tall at the end.',
      },
      { time: 735, text: 'Walk forward 4 slow steps. Arms low. Quiet feet.' },
      { time: 750, text: 'Walk back 4 slow steps. Knees over toes.' },
      {
        time: 765,
        text: 'Arms to first. Walk side 4 steps right, then left. Shoulders down.',
      },
      {
        time: 780,
        text: 'Tiptoe walk is releve. Rise to the balls of your feet, tiny steps, lower with control.',
      },
      {
        time: 795,
        text: 'Tiptoe walk forward 6 counts. Breathe out as you lower.',
      },
      { time: 810, text: 'Tiptoe walk back 6 counts. Neck long, chest calm.' },
      { time: 825, text: 'Reminder. Two tracks, knees soft, quiet landings.' },

      // 18:00-21:00 Arm waves, circles, and port de bras flow
      {
        time: 840,
        text: 'Arm waves. Draw a gentle wave with your fingers, then your wrist, then your elbow.',
      },
      { time: 855, text: 'Repeat the wave both arms. Move slow and smooth.' },
      {
        time: 870,
        text: 'Arm circles low and wide. Elbows soft, shoulders down.',
      },
      {
        time: 885,
        text: 'Circle arms up to fifth, then open to second, then float to low arms.',
      },
      {
        time: 900,
        text: 'Port de bras flow. Low to first to second to fifth to second to low. Breathe steady.',
      },
      { time: 915, text: 'Head follows the hand gently. Eyes soft.' },
      { time: 930, text: 'Reminder. Tall spine, tummy snug, fingers long.' },
      { time: 945, text: 'Freeze in first. Hold still for 3, 2, 1.' },

      // 21:00-24:00 Balance practice for grace
      {
        time: 960,
        text: 'Releve balance. Rise tall, arms in first, hold for 3, 2, 1, lower slow.',
      },
      {
        time: 975,
        text: 'Repeat releve balance. Quiet toes. Knees and toes match.',
      },
      {
        time: 990,
        text: 'Coupe line. Lift one foot to your ankle, toes kiss the ankle, knee points to the side.',
      },
      {
        time: 1005,
        text: 'Balance in coupe right for 3, 2, 1. Lower. Then left for 3, 2, 1.',
      },
      {
        time: 1020,
        text: 'Arabesque line low. Back leg long and low, hips square, chest calm, arm forward soft.',
      },
      {
        time: 1035,
        text: 'Hold arabesque right for 3, 2, 1. Lower. Then left for 3, 2, 1.',
      },
      {
        time: 1050,
        text: 'Reminder. Head over hips, shoulders down, breathe steady.',
      },
      { time: 1065, text: 'Stand tall. Small smile. Quiet feet.' },

      // 24:00-27:00 Sways, turns prep, quarter turns
      {
        time: 1080,
        text: 'Sway right and left with arms in second. Soft knees. Smooth breath.',
      },
      {
        time: 1095,
        text: 'Add a rise. Sway and rise tiny, then lower with control.',
      },
      {
        time: 1110,
        text: 'Spotting. Pick a point with your eyes. Keep your eyes on it as long as you can.',
      },
      {
        time: 1125,
        text: 'Quarter turn right. Tiny turn, eyes snap back to the point. Tall spine.',
      },
      {
        time: 1140,
        text: 'Quarter turn left. Tiny turn, eyes back to the point. Shoulders down.',
      },
      {
        time: 1155,
        text: 'Repeat quarter turns both ways. Control your breath.',
      },
      { time: 1170, text: 'Reminder. Two tracks, knees soft, head over hips.' },
      {
        time: 1185,
        text: 'Hold first. Breathe in for 3, 2, 1. Out for 3, 2, 1.',
      },

      // 27:00-30:00 Beginner grace combo and cooldown
      {
        time: 1200,
        text: 'Teach the grace combo. Ballet walk 4, arm wave, tendu side close, port de bras, small sway.',
      },
      {
        time: 1215,
        text: 'Do the combo slow one time. Smooth and calm. Quiet feet.',
      },
      {
        time: 1230,
        text: 'Add head. Look with your hand, then back to center. Keep chin level.',
      },
      {
        time: 1245,
        text: 'Do the combo again. Breathe in on arms up, out on arms down.',
      },
      {
        time: 1260,
        text: 'Mirror the combo to the other side. Same steps, switch directions.',
      },
      {
        time: 1275,
        text: 'Reminder. Tall spine, knees and toes match, fingers long.',
      },
      {
        time: 1290,
        text: 'Performance try. Do both sides without stopping. Flow like water.',
      },
      {
        time: 1305,
        text: 'Finish in first. Releve balance for 3, 2, 1. Lower slow.',
      },

      // Cooldown and reverence to complete 30:00
      {
        time: 1320,
        text: 'Butterfly stretch. Sit tall, feet together, knees wide, hold ankles. Hold for 3, 2, 1.',
      },
      {
        time: 1335,
        text: 'Seated hamstring fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      {
        time: 1350,
        text: 'Figure four right small. Ankle over knee, sit tall. Hold for 3, 2, 1.',
      },
      {
        time: 1365,
        text: 'Figure four left small. Breathe easy. Hold for 3, 2, 1.',
      },
      {
        time: 1380,
        text: 'Roll to stand. First position. Tall spine. Quiet feet.',
      },
      {
        time: 1395,
        text: 'Port de bras slow. Low to first to second to fifth, then down.',
      },
      {
        time: 1410,
        text: 'Reverence. A small bow or curtsey to say thank you. Hold for 3, 2, 1.',
      },
      {
        time: 1425,
        text: 'Posture scan. Head over hips, shoulders down, chin level.',
      },
      { time: 1440, text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1.' },
      { time: 1455, text: 'You moved with grace and care. Small smile.' },
      { time: 1470, text: 'Stand tall in first for 3, 2, 1. Arms low.' },
      { time: 1485, text: 'Deep breath in. Long breath out. Calm body.' },
      { time: 1500, text: 'Session complete hold. Quiet feet for 3, 2, 1.' },
      {
        time: 1503,
        text: 'Session complete. Beginner Ballet Grace finished. Great work today.',
      },
    ],
    timeTotal: 1506,
    equipment: 'None',
  },
  // INTERMEDIATE BALLET GRACE (45 minutes) Done
  {
    id: 2197,
    name: 'Intermediate Ballet Grace',
    categories: ['Grace', 'intermediate'],
    sports: ['ballet'],
    difficulty: 'intermediate',
    skill: 'Grace',
    points: 40,
    instructions:
      'Intermediate Ballet Grace builds on your basic graceful movements with harder moves and better flow. This 40-minute session helps you practice better graceful moves, fun combinations, and cool flow patterns.',
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, breathing, turnout safety
      {
        time: 0,
        text: 'Stand tall in first. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Posture check. Tall spine, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 48,
        text: 'Turnout safety. Toes turn out a little from your hips. Knees point the same way as toes.',
      },
      {
        time: 72,
        text: 'Weight split even on both feet. Heels touch the floor, toes light and long.',
      },
      {
        time: 96,
        text: 'Arms low. Fingers long like you hold bubbles. Wrists soft, elbows rounded.',
      },
      {
        time: 120,
        text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1. Quiet feet.',
      },
      {
        time: 144,
        text: 'Reminder. Two tracks, knees and toes match, head over hips.',
      },

      // 3:00-6:00 Port de bras basics with grace
      {
        time: 168,
        text: 'Port de bras means carry the arms. Move slow and smooth like water.',
      },
      {
        time: 192,
        text: 'Low arms to first. Pause tall. Open to second. Pause tall. Lower to low arms.',
      },
      {
        time: 216,
        text: 'Add breath. In as arms rise, out as arms float down. Shoulders stay down.',
      },
      {
        time: 240,
        text: 'First to second to fifth to second to low. Elbows lifted, fingers soft.',
      },
      {
        time: 264,
        text: 'Side bend right small with arms in second. Tall spine. Come up slow.',
      },
      {
        time: 288,
        text: 'Side bend left small. Keep hips level. Come up slow.',
      },

      // 6:00-9:00 Head, eyes, and epaulment for grace
      {
        time: 312,
        text: 'Head and eyes. Look right softly, back to center. Then left, back to center.',
      },
      {
        time: 336,
        text: 'Head tilt. Ear to shoulder tiny, lift tall. Other side tiny, lift tall.',
      },
      {
        time: 360,
        text: 'Epaulment means gentle shoulder shaping. Turn your shoulders a little, keep hips steady.',
      },
      {
        time: 384,
        text: 'Epaulment right with soft eyes. Return to square. Then left, return.',
      },
      { time: 408, text: 'Reminder. Neck long, chest calm, breath smooth.' },
      { time: 432, text: 'Freeze in first. Hold still for 3, 2, 1.' },

      // 9:00-12:00 Graceful plié with arms
      {
        time: 456,
        text: 'Plie means bend. In first, bend knees small, heels stay down, then stretch tall.',
      },
      {
        time: 480,
        text: 'Add arms. Plie with arms to first, stretch and open to second.',
      },
      {
        time: 504,
        text: 'Two slow plies in first. Knees over toes. Head over hips.',
      },
      {
        time: 528,
        text: 'Open to second position. Feet wider, toes out, two tracks stay.',
      },
      {
        time: 552,
        text: 'Demi plie in second two times. Knees track with toes. Spine tall.',
      },
      {
        time: 576,
        text: 'Small grand plie in second. Heels stay down. Breathe out as you rise.',
      },

      // 12:00-15:00 Tendu for smooth lines, temps lié for weight shift
      {
        time: 600,
        text: 'Tendu means brush to a point. Brush toes out, knee straight, then close.',
      },
      {
        time: 624,
        text: 'From first. Tendu front right, close. Left, close. Toes long.',
      },
      {
        time: 648,
        text: 'Tendu side right, close. Left, close. Heel forward.',
      },
      {
        time: 672,
        text: 'Tendu back right, close. Left, close. Heel up in back.',
      },
      {
        time: 696,
        text: 'Temps lie means time linked. Shift weight side with an arm float, then return.',
      },
      {
        time: 720,
        text: 'Temps lie right and back to center. Then left and back. Smooth breath.',
      },

      // 15:00-18:00 Ballet walk and tiptoe walk with arm pathways
      {
        time: 744,
        text: 'Ballet walk. Step with toes pointed, roll through your foot, stand tall at the end.',
      },
      { time: 768, text: 'Walk forward 4 slow steps. Arms low. Quiet feet.' },
      { time: 792, text: 'Walk back 4 slow steps. Knees over toes.' },
      {
        time: 816,
        text: 'Arms to first. Walk side 4 steps right, then left. Shoulders down.',
      },
      {
        time: 840,
        text: 'Tiptoe walk is releve. Rise to the balls of your feet, tiny steps, lower with control.',
      },
      {
        time: 864,
        text: 'Tiptoe walk forward 6 counts. Breathe out as you lower.',
      },

      // 18:00-21:00 Waltz step and balancé for flowing grace
      {
        time: 888,
        text: 'Waltz step. Step, side, close with a tiny rise. Arms round and soft.',
      },
      { time: 912, text: 'Waltz right 2 bars, then left 2 bars. Quiet feet.' },
      {
        time: 936,
        text: 'Balance step. Rock side, back, front with open arms.',
      },
      {
        time: 960,
        text: 'Balance right, then left. Turn your body a little as you go.',
      },
      {
        time: 984,
        text: 'Add head. Look with the hand, return to center. Chin level.',
      },
      {
        time: 1008,
        text: 'Reminder. Two tracks, knees and toes match, breath steady.',
      },

      // 21:00-24:00 Pas de bourrée and chassé glide
      {
        time: 1032,
        text: 'Pas de bourree. Back, side, front. Tiny triangle. Light and quick.',
      },
      {
        time: 1056,
        text: 'Pas de bourree right, then left. Arms low and calm.',
      },
      {
        time: 1080,
        text: 'Chasse means to chase. Step, close, step. Feet kiss in the middle. Glide.',
      },
      { time: 1104, text: 'Chasse right small, then left small. Land soft.' },
      { time: 1128, text: 'Link. Pas de bourree to chasse. Smooth and quiet.' },
      {
        time: 1152,
        text: 'Reminder. Head over hips, shoulders down, fingers soft.',
      },

      // 24:00-27:00 Sways and cambre side and small back (safe)
      {
        time: 1176,
        text: 'Sway right and left with arms in second. Soft knees. Smooth breath.',
      },
      {
        time: 1200,
        text: 'Cambre side. Tip from ribs, not hips. Keep tummy snug. Return tall.',
      },
      {
        time: 1224,
        text: 'Cambre other side. Tiny, safe, smooth. Return tall.',
      },
      {
        time: 1248,
        text: 'Small cambre back. Lift chest a little, keep neck long, do not crunch. Return.',
      },
      {
        time: 1272,
        text: 'Arms trace a rainbow to fifth, open to second, then float to low.',
      },
      {
        time: 1296,
        text: 'Reminder. Turnout small and safe. Knees and toes match.',
      },

      // 27:00-30:00 Adagio lines: developpé and arabesque low
      {
        time: 1320,
        text: 'Developpe means unfold. Knee to passe, stretch the leg, then close with control.',
      },
      {
        time: 1344,
        text: 'Developpe front right slow. Hips square. Breathe out as you stretch.',
      },
      {
        time: 1368,
        text: 'Developpe side right slow. Toes lead, knee up, close smooth.',
      },
      {
        time: 1392,
        text: 'Developpe back right slow. Heel up in back, chest calm.',
      },
      {
        time: 1416,
        text: 'Left side. Front, side, back, all slow and smooth.',
      },
      {
        time: 1440,
        text: 'Arabesque line low. Back leg long and low, arm forward soft, hips square.',
      },

      // 30:00-33:00 Spotting and soutenu half turns, chaines small
      {
        time: 1464,
        text: 'Spotting. Pick a point with your eyes. Turn your body and snap your head back to the point.',
      },
      {
        time: 1488,
        text: 'Soutenu half turn. Close feet, turn around on both feet, spot, then lower.',
      },
      {
        time: 1512,
        text: 'Do two soutenu half turns. Shoulders down, neck long.',
      },
      {
        time: 1536,
        text: 'Chaines are tiny quick turns on two feet. Step close, turn, spot, repeat.',
      },
      {
        time: 1560,
        text: 'Chaines across small. Right 6 counts. Arms in first.',
      },
      { time: 1584, text: 'Chaines back small. Left 6 counts. Quiet feet.' },

      // 33:00-36:00 Grace combo teach, right side
      {
        time: 1608,
        text: 'Combo teach right. Tendu side close, pas de bourree right, balance left, port de bras to fifth.',
      },
      {
        time: 1632,
        text: 'Add. Waltz step forward, sway, temps lie side, close to first.',
      },
      {
        time: 1656,
        text: 'Run right combo once. Small and clean. Breath steady.',
      },
      {
        time: 1680,
        text: 'Polish arms. Fingers long, elbows lifted, shoulders down.',
      },
      { time: 1704, text: 'Run right combo again. Quiet feet, tall spine.' },
      {
        time: 1728,
        text: 'Reminder. Two tracks, knees and toes match, head over hips.',
      },

      // 36:00-39:00 Grace combo teach, left side
      {
        time: 1752,
        text: 'Combo teach left. Tendu side close, pas de bourree left, balance right, port de bras to fifth.',
      },
      {
        time: 1776,
        text: 'Add. Waltz step forward, sway, temps lie side, close to first.',
      },
      { time: 1800, text: 'Run left combo once. Smooth and calm.' },
      {
        time: 1824,
        text: 'Polish head and eyes. Look with the hand, return to center.',
      },
      { time: 1848, text: 'Run left combo again. Breathe with the flow.' },
      { time: 1872, text: 'Reminder. Neck long, chest calm, fingers soft.' },

      // 39:00-42:00 Performance flow both sides
      {
        time: 1896,
        text: 'Performance flow. Do right combo then left combo without stopping.',
      },
      { time: 1920, text: 'Keep arms round and smooth. Spot gently on turns.' },
      {
        time: 1944,
        text: 'Breath pattern. In on arms up, out on arms down. Quiet feet.',
      },
      { time: 1968, text: 'Repeat both sides. Small and clean. Tall spine.' },
      {
        time: 1992,
        text: 'Finish in first. Releve balance for 3, 2, 1. Lower slow.',
      },
      {
        time: 2016,
        text: 'Reminder. Turnout small and safe. Knees and toes match.',
      },

      // 42:00-45:00 Cooldown and reverence
      {
        time: 2040,
        text: 'Butterfly stretch. Sit tall, feet together, knees wide, hold ankles. Hold for 3, 2, 1.',
      },
      {
        time: 2064,
        text: 'Seated hamstring fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      {
        time: 2088,
        text: 'Figure four right small. Ankle over knee, sit tall. Hold for 3, 2, 1.',
      },
      {
        time: 2112,
        text: 'Figure four left small. Breathe easy. Hold for 3, 2, 1.',
      },
      { time: 2136, text: 'Roll to stand. First position. Tall spine.' },
      {
        time: 2160,
        text: 'Port de bras slow. Low to first to second to fifth, then down.',
      },
      {
        time: 2184,
        text: 'Reverence. A small bow or curtsey to say thank you. Hold for 3, 2, 1.',
      },
      {
        time: 2208,
        text: 'Posture scan. Head over hips, shoulders down, chin level.',
      },
      { time: 2232, text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1.' },
      { time: 2256, text: 'You moved with grace and care. Small smile.' },
      { time: 2280, text: 'Stand tall in first for 3, 2, 1. Arms low.' },
      { time: 2304, text: 'Deep breath in. Long breath out. Calm body.' },
      {
        time: 2306,
        text: 'Session complete. Intermediate Ballet Grace finished. Great work today.',
      },
    ],
    timeTotal: 2310,
    equipment: 'None',
  },
  // ADVANCED BALLET GRACE (45 minutes)
  {
    id: 2198,
    name: 'Advanced Ballet Grace',
    categories: ['Grace', 'advanced'],
    sports: ['ballet'],
    difficulty: 'advanced',
    skill: 'Grace',
    points: 38,
    instructions:
      'Advanced Ballet Grace teaches you fancy graceful movements and tricky flow coordination. This 38-minute session helps you practice cool graceful moves, fun combinations, and expert flow exercises.',
    timedInstructions: [
      // 0:00–3:00 Arrive, posture, turnout, breath, grace focus
      {
        time: 0,
        text: 'Stand tall in first. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Posture check. Tall spine, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 48,
        text: 'Turnout safety. Toes turn out from your hips. Knees point the same way as toes.',
      },
      {
        time: 72,
        text: 'Grace focus. Move smooth like water. Arms calm, fingers long, neck tall.',
      },
      {
        time: 96,
        text: 'Weight even on both feet. Heels press the floor. Toes light and long.',
      },
      {
        time: 120,
        text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1. Quiet feet.',
      },

      // 3:00–6:00 Grand port de bras and epaulement basics
      {
        time: 144,
        text: 'Port de bras means carry the arms. Move slow and smooth.',
      },
      {
        time: 168,
        text: 'Grand port de bras. Low to first, open to second, lift to fifth, open to second, and down.',
      },
      {
        time: 192,
        text: 'Add breath. Breathe in as arms rise, out as arms float down. Shoulders stay down.',
      },
      {
        time: 216,
        text: 'Epaulement means gentle shoulder shaping. Turn your shoulders a little, hips stay steady.',
      },
      {
        time: 240,
        text: 'Epaulement right with soft eyes, return to square. Then left, return. Neck long.',
      },
      {
        time: 264,
        text: 'Reminder. Two tracks, knees and toes match, head over hips.',
      },

      // 6:00–9:00 Head, eyes, and rib control for grace
      {
        time: 288,
        text: 'Head and eyes. Look right softly, back to center. Then left, center. Keep chin level.',
      },
      {
        time: 312,
        text: 'Head tilt tiny. Ear to shoulder, lift tall. Other side tiny, lift tall.',
      },
      {
        time: 336,
        text: 'Rib control. Keep ribs hugged in. Chest calm as arms move.',
      },
      {
        time: 360,
        text: 'Arms to first and open to second while ribs stay quiet. Breathe steady.',
      },
      {
        time: 384,
        text: 'Hold first for 3, 2, 1. Fingers soft, wrists gentle.',
      },
      { time: 408, text: 'Reminder. Tall spine, tummy snug, shoulders down.' },

      // 9:00–12:00 Pliés with graceful arms and small cambré
      {
        time: 432,
        text: 'Plie means bend. In first, bend knees small, heels down, then stretch tall.',
      },
      {
        time: 456,
        text: 'Two demi plies in first with arms to first, then open to second.',
      },
      {
        time: 480,
        text: 'Grand plie small and safe. Heels stay down, then lift a little, then press and stretch.',
      },
      {
        time: 504,
        text: 'Open to second. Feet wider, two tracks stay. Two demi plies with arms calm.',
      },
      {
        time: 528,
        text: 'Small cambré side and return. Then other side and return. Neck long.',
      },
      {
        time: 552,
        text: 'Small cambré back. Lift your chest a little, no crunch. Return tall.',
      },

      // 12:00–15:00 Tendu lines and temps lié flow
      {
        time: 576,
        text: 'Tendu means brush to a point. Brush out with a straight knee, then close.',
      },
      {
        time: 600,
        text: 'Tendu front right, close. Left, close. Toes long, heel forward.',
      },
      { time: 624, text: 'Tendu side right, close. Left, close. Hips steady.' },
      {
        time: 648,
        text: 'Tendu back right, close. Left, close. Heel up in back.',
      },
      {
        time: 672,
        text: 'Temps lie means time linked. Shift weight side with arm float, then return.',
      },
      {
        time: 696,
        text: 'Temps lie right and back to center. Then left and back. Smooth breath.',
      },

      // 15:00–18:00 Adagio lines: developpé with breath
      {
        time: 720,
        text: 'Developpe means unfold. Knee to passe, stretch the leg, then close with control.',
      },
      {
        time: 744,
        text: 'Developpe front right slow. Hips square, neck long. Breathe out as you stretch.',
      },
      {
        time: 768,
        text: 'Developpe side right slow. Toes lead, knee up, close smooth.',
      },
      {
        time: 792,
        text: 'Developpe back right slow. Heel up in back, chest calm.',
      },
      { time: 816, text: 'Left side. Front, side, back, all slow and smooth.' },
      {
        time: 840,
        text: 'Reminder. Knees and toes match. Shoulders down. Breathe steady.',
      },

      // 18:00–21:00 Arabesque and attitude lines with balances
      {
        time: 864,
        text: 'Arabesque line. Back leg long, hips square, arm forward soft.',
      },
      {
        time: 888,
        text: 'Arabesque right hold for 3, 2, 1. Lower with control. Then left hold for 3, 2, 1.',
      },
      {
        time: 912,
        text: 'Attitude is a bent knee shape. Lift to front attitude small, then lower.',
      },
      {
        time: 936,
        text: 'Attitude back small. Shoulders down, chin level, tummy snug.',
      },
      {
        time: 960,
        text: 'Releve balance in first for 3, 2, 1. Lower slow. Quiet feet.',
      },
      {
        time: 984,
        text: 'Reminder. Head over hips. Breathe smooth. Fingers long.',
      },

      // 21:00–24:00 Promenade prep and slow turn
      {
        time: 1008,
        text: 'Promenade means slow turn in place while keeping your shape.',
      },
      {
        time: 1032,
        text: 'From arabesque line, tiny steps of the heel to turn slow. Keep hips square and head calm.',
      },
      {
        time: 1056,
        text: 'Promenade right quarter turn. Hold shape for 3, 2, 1. Lower.',
      },
      {
        time: 1080,
        text: 'Promenade left quarter turn. Keep neck long. Lower with control.',
      },
      {
        time: 1104,
        text: 'Repeat a tiny promenade on your best side. Spot gently. Calm breath.',
      },
      {
        time: 1128,
        text: 'Reminder. Two tracks, knees and toes match, chest calm.',
      },

      // 24:00–27:00 Waltz travel and balancé with epaulement
      {
        time: 1152,
        text: 'Waltz step. Step, side, close with a tiny rise. Arms round and soft.',
      },
      {
        time: 1176,
        text: 'Waltz right 2 bars, then left 2 bars. Quiet feet. Breathe with the phrase.',
      },
      {
        time: 1200,
        text: 'Balance step. Rock side, back, front with open arms.',
      },
      {
        time: 1224,
        text: 'Balance right, then left. Add epaulement small and soft.',
      },
      {
        time: 1248,
        text: 'Add head. Look with the hand, return to center. Chin level.',
      },
      {
        time: 1272,
        text: 'Reminder. Tall spine, shoulders down, fingers soft.',
      },

      // 27:00–30:00 Pas de bourrée, chassé, and glide link
      {
        time: 1296,
        text: 'Pas de bourree. Back, side, front. Tiny triangle. Light and quick.',
      },
      {
        time: 1320,
        text: 'Pas de bourree right, then left. Arms low and calm.',
      },
      {
        time: 1344,
        text: 'Chasse means to chase. Step, close, step. Feet kiss in the middle. Glide.',
      },
      { time: 1368, text: 'Chasse right small, then left small. Land soft.' },
      {
        time: 1392,
        text: 'Link. Pas de bourree to chasse with smooth arms. Quiet feet.',
      },
      {
        time: 1416,
        text: 'Reminder. Head over hips, breath steady, knees over toes.',
      },

      // 30:00–33:00 Turns for grace: soutenu, chainés, piqué small
      {
        time: 1440,
        text: 'Soutenu turn. Close feet, turn around on both feet, spot, then lower.',
      },
      { time: 1464, text: 'Do two soutenu turns with calm arms. Neck long.' },
      {
        time: 1488,
        text: 'Chaines are tiny quick turns on two feet. Step close, turn, spot, repeat.',
      },
      {
        time: 1512,
        text: 'Chaines across small to the right 6 counts. Arms in first.',
      },
      {
        time: 1536,
        text: 'Chaines back small to the left 6 counts. Quiet feet.',
      },
      {
        time: 1560,
        text: 'Pique turn. Step on a straight leg to releve, other leg to low passe, spot, close.',
      },

      // 33:00–36:00 Advanced grace combo teach, right side
      {
        time: 1584,
        text: 'Combo teach right. Tendu side close, pas de bourree right, balance left with epaulement, port de bras to fifth.',
      },
      {
        time: 1608,
        text: 'Add. Waltz step forward, temps lie side, soutenu turn half, close to first.',
      },
      {
        time: 1632,
        text: 'Run right combo once. Small and clean. Breathe steady.',
      },
      {
        time: 1656,
        text: 'Polish arms. Fingers long, elbows lifted, shoulders down.',
      },
      { time: 1680, text: 'Run right combo again. Quiet feet, tall spine.' },
      {
        time: 1704,
        text: 'Reminder. Knees and toes match, head over hips, neck long.',
      },

      // 36:00–39:00 Advanced grace combo teach, left side
      {
        time: 1728,
        text: 'Combo teach left. Tendu side close, pas de bourree left, balance right, port de bras to fifth.',
      },
      {
        time: 1752,
        text: 'Add. Waltz step forward, temps lie side, soutenu turn half, close to first.',
      },
      {
        time: 1776,
        text: 'Run left combo once. Smooth and calm. Breathe with the phrase.',
      },
      {
        time: 1800,
        text: 'Polish head and eyes. Look with the hand, return to center.',
      },
      { time: 1824, text: 'Run left combo again. Arms round and soft.' },
      {
        time: 1848,
        text: 'Reminder. Turnout small and safe. Chest calm. Quiet feet.',
      },

      // 39:00–42:00 Performance flow both sides with expression
      {
        time: 1872,
        text: 'Performance flow. Do right combo then left combo without stopping.',
      },
      {
        time: 1896,
        text: 'Add expression. Soft eyes, tiny smile, breathe with the music.',
      },
      {
        time: 1920,
        text: 'Repeat both sides. Keep arms smooth, shoulders down.',
      },
      {
        time: 1944,
        text: 'Finish in first. Releve balance for 3, 2, 1. Lower slow.',
      },
      {
        time: 1968,
        text: 'Reminder. Two tracks, knees and toes match, head over hips.',
      },
      { time: 1992, text: 'Calm breath. In for 3, 2, 1. Out for 3, 2, 1.' },

      // 42:00–45:00 Cooldown and reverence
      {
        time: 2016,
        text: 'Butterfly stretch. Sit tall, feet together, knees wide, hold ankles. Hold for 3, 2, 1.',
      },
      {
        time: 2040,
        text: 'Seated hamstring fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      {
        time: 2064,
        text: 'Figure four right small. Ankle over knee, sit tall. Hold for 3, 2, 1.',
      },
      {
        time: 2088,
        text: 'Figure four left small. Breathe easy. Hold for 3, 2, 1.',
      },
      {
        time: 2112,
        text: 'Roll to stand. First position. Tall spine. Quiet feet.',
      },
      {
        time: 2136,
        text: 'Grand port de bras slow. Low to first to second to fifth, then down.',
      },
      {
        time: 2160,
        text: 'Reverence. A small bow or curtsey to say thank you. Hold for 3, 2, 1.',
      },
      {
        time: 2184,
        text: 'Posture scan. Head over hips, shoulders down, chin level.',
      },
      {
        time: 2208,
        text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1. Calm body.',
      },
      { time: 2232, text: 'You danced with grace and care. Small smile.' },
      { time: 2256, text: 'Stand tall in first for 3, 2, 1. Arms low.' },
      { time: 2280, text: 'Deep breath in. Long breath out. Quiet feet.' },
      {
        time: 2284,
        text: 'Session complete. Advanced Ballet Grace finished. Great work today.',
      },
    ],
    timeTotal: 2292,
    equipment: 'None',
  },
  // COMPLETE BALLET (60 minutes)
  {
    id: 2199,
    name: 'Complete Ballet',
    categories: ['Complete', 'advanced'],
    sports: ['ballet'],
    difficulty: 'advanced',
    skill: 'Complete',
    points: 60,
    instructions:
      'Complete Ballet combines all your ballet skills into one amazing session. This 60-minute session helps you practice all ballet moves, fun combinations, and expert coordination exercises.',
    timedInstructions: [
      // 0:00–4:00 Arrive, posture, turnout, breath
      {
        time: 0,
        text: 'Stand tall in first. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Posture check. Tall spine, tummy snug, shoulders down, chin level, eyes forward.',
      },
      {
        time: 48,
        text: 'Turnout safety. Toes turn out from your hips. Knees point the same way as toes. Never twist your knees.',
      },
      {
        time: 72,
        text: 'Arms low. Fingers long like you hold bubbles. Wrists soft, elbows rounded.',
      },
      {
        time: 96,
        text: 'Weight even on both feet. Heels press the floor. Toes light and long.',
      },
      {
        time: 120,
        text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1. Quiet feet.',
      },
      {
        time: 144,
        text: 'Reminder. Two tracks, knees and toes match, head over hips.',
      },
      {
        time: 168,
        text: 'Neck long. Crown of your head lifts to the sky. Chest calm, ribs hugged in.',
      },
      {
        time: 192,
        text: 'Small nod yes, small look side to side. Shoulders stay down.',
      },
      { time: 216, text: 'Freeze tall for 3, 2, 1. Breathe smooth.' },

      // 4:00–10:00 Barre plies first, second, fifth with arms
      {
        time: 240,
        text: 'Plie means bend. In first, bend knees small, heels stay down, then stretch tall.',
      },
      {
        time: 264,
        text: 'Demi plie in first two times. Knees over toes. Head over hips.',
      },
      {
        time: 288,
        text: 'Grand plie small and safe. Heels stay down, lift a little, press and stretch.',
      },
      {
        time: 312,
        text: 'Open to second. Feet wider, two tracks stay. Arms float to second.',
      },
      {
        time: 336,
        text: 'Demi plie in second two times. Knees track with toes. Spine tall.',
      },
      {
        time: 360,
        text: 'Small grand plie in second. Breathe out as you rise.',
      },
      { time: 384, text: 'Close to fifth. Heels kiss toes. Hips quiet.' },
      {
        time: 408,
        text: 'Two demi plies in fifth. Knees and toes match. Shoulders down.',
      },
      {
        time: 432,
        text: 'Grand plie in fifth tiny and safe. Control. Then stretch tall.',
      },
      { time: 456, text: 'Reminder. Turnout small and safe. Quiet feet.' },

      // 10:00–16:00 Tendu and degage en croix, temps lié
      {
        time: 480,
        text: 'Tendu means brush to a point. Brush toes out, knee straight, then close.',
      },
      {
        time: 504,
        text: 'From fifth. Tendu front right, close. Left, close. Toes long, heel forward.',
      },
      { time: 528, text: 'Tendu side right, close. Left, close. Hips steady.' },
      {
        time: 552,
        text: 'Tendu back right, close. Left, close. Heel up in back.',
      },
      {
        time: 576,
        text: 'Degage is a quick brush that leaves the floor. Small lift and close.',
      },
      {
        time: 600,
        text: 'Degage front 4 small. Then side 4 small. Point and close.',
      },
      { time: 624, text: 'Degage back 4 small. Spine tall. Neck long.' },
      {
        time: 648,
        text: 'Temps lie means time linked. Shift weight side with arm float, then return.',
      },
      {
        time: 672,
        text: 'Temps lie right and back to center. Then left and back. Smooth breath.',
      },
      {
        time: 696,
        text: 'Reminder. Knees and toes match. Two tracks. Shoulders down.',
      },

      // 16:00–22:00 Rond de jambe, fondu, frappe
      {
        time: 720,
        text: 'Rond de jambe a terre. Draw a half circle with your toes on the floor.',
      },
      {
        time: 744,
        text: 'En dehors means out. Front to side to back, close. Hips quiet.',
      },
      {
        time: 768,
        text: 'Right leg en dehors 4 slow. Knees straight, toes long.',
      },
      {
        time: 792,
        text: 'En dedans means in. Back to side to front, close. Breathe steady.',
      },
      {
        time: 816,
        text: 'Right leg en dedans 4 slow. Spine tall, ribs hugged in.',
      },
      { time: 840, text: 'Left leg en dehors 4 slow. Heel forward.' },
      { time: 864, text: 'Left leg en dedans 4 slow. Calm arms.' },
      {
        time: 888,
        text: 'Fondu means to melt. Bend both legs, then stretch together, leg to a point.',
      },
      {
        time: 912,
        text: 'Fondu front right two times, then side two, then back two. Control.',
      },
      {
        time: 936,
        text: 'Frappe means to strike. Flex at the ankle, then strike to a point and close.',
      },
      {
        time: 960,
        text: 'Frappe right 4 quick. Then left 4 quick. Knees and toes match.',
      },
      { time: 984, text: 'Reminder. Quiet hips. Neck long. Breathe smooth.' },

      // 22:00–28:00 Developpe and petit battement, balance
      {
        time: 1008,
        text: 'Developpe means unfold. Knee to passe, stretch the leg, then close with control.',
      },
      {
        time: 1032,
        text: 'Developpe front right slow. Hips square. Breathe out as you stretch.',
      },
      {
        time: 1056,
        text: 'Developpe side right slow. Toes lead, knee up, close smooth.',
      },
      {
        time: 1080,
        text: 'Developpe back right slow. Heel up in back, chest calm.',
      },
      {
        time: 1104,
        text: 'Developpe left side. Front, side, back, all slow and smooth.',
      },
      {
        time: 1128,
        text: 'Petit battement. Tiny switches at the ankle. Thighs still, knee points out.',
      },
      {
        time: 1152,
        text: 'Petit battement 8 counts right. Then 8 counts left. Light and quick.',
      },
      {
        time: 1176,
        text: 'Releve balance in first for 3, 2, 1. Lower slow. Quiet feet.',
      },
      {
        time: 1200,
        text: 'Releve balance in fifth for 3, 2, 1. Lower slow. Shoulders down.',
      },
      {
        time: 1224,
        text: 'Reminder. Two tracks, knees and toes match, head over hips.',
      },

      // 28:00–32:00 Grand battement en croix
      {
        time: 1248,
        text: 'Grand battement is a big brush kick. Brush up with a straight knee, control down, and close.',
      },
      {
        time: 1272,
        text: 'Grand battement front right 4 controlled. Quiet hips.',
      },
      {
        time: 1296,
        text: 'Grand battement side right 4 controlled. Heel leads.',
      },
      {
        time: 1320,
        text: 'Grand battement back right 4 controlled. Heel up in back.',
      },
      {
        time: 1344,
        text: 'Left leg. Front 4, side 4, back 4. Control the return.',
      },
      {
        time: 1368,
        text: 'Reminder. Tall spine. Breathe out on the brush. Land soft.',
      },

      // 32:00–38:00 Center adagio: arabesque, attitude, promenade
      {
        time: 1392,
        text: 'Center. Arabesque line. Back leg long, hips square, chest calm, arm forward soft.',
      },
      {
        time: 1416,
        text: 'Arabesque right. Hold for 3, 2, 1. Lower with control.',
      },
      {
        time: 1440,
        text: 'Arabesque left. Hold for 3, 2, 1. Lower with control.',
      },
      {
        time: 1464,
        text: 'Attitude means a bent knee shape. Lift to front attitude small. Hips steady.',
      },
      {
        time: 1488,
        text: 'Attitude front right hold 3, 2, 1. Lower. Then left hold 3, 2, 1.',
      },
      {
        time: 1512,
        text: 'Attitude back both sides. Shoulders down, chin level.',
      },
      {
        time: 1536,
        text: 'Promenade means slow turn in place while keeping your shape.',
      },
      {
        time: 1560,
        text: 'From arabesque, tiny steps of the heel to turn slow. Keep hips square and head calm.',
      },
      {
        time: 1584,
        text: 'Promenade right quarter turn. Hold shape for 3, 2, 1. Lower.',
      },
      {
        time: 1608,
        text: 'Promenade left quarter turn. Neck long. Lower with control.',
      },
      {
        time: 1632,
        text: 'Reminder. Two tracks, breathe steady, fingers long.',
      },

      // 38:00–44:00 Pirouettes: prep, half turns, singles and doubles
      {
        time: 1656,
        text: 'Passe means toe at your knee. Turnout small and safe.',
      },
      {
        time: 1680,
        text: 'Spotting. Pick a point with your eyes. Turn your body and snap your head back to the point.',
      },
      {
        time: 1704,
        text: 'Pirouette prep. From fourth, plie, push to releve, knee to passe, lower and close.',
      },
      {
        time: 1728,
        text: 'Half turn en dehors right two times. Spot each time. Land soft in fourth.',
      },
      {
        time: 1752,
        text: 'Half turn en dehors left two times. Keep spine tall.',
      },
      {
        time: 1776,
        text: 'Single pirouette en dehors right. Plie, push, spot, finish in fourth.',
      },
      {
        time: 1800,
        text: 'Single pirouette en dehors left. Plie, push, spot, finish clean.',
      },
      {
        time: 1824,
        text: 'Try one clean double on your best side. Only if stable and safe.',
      },
      {
        time: 1848,
        text: 'Balance in passe right for 3, 2, 1. Lower. Then left for 3, 2, 1.',
      },
      {
        time: 1872,
        text: 'Reminder. Knees over toes in plie. Head over hips. Calm arms.',
      },

      // 44:00–50:00 Petit allegro: echappe, changement, glissade, assemble, jete petit, pas de chat
      {
        time: 1896,
        text: 'Echappe saute. Jump from closed to open, heels press, then close.',
      },
      {
        time: 1920,
        text: 'Do 2 echappe, then 2 changement. Land through toes then heels.',
      },
      {
        time: 1944,
        text: 'Changement means change your feet in the air. Knees point with toes.',
      },
      {
        time: 1968,
        text: 'Glissade. Brush and glide to the side, feet close in the air, land in fifth.',
      },
      {
        time: 1992,
        text: 'Assemble. Brush one foot, jump and assemble both feet to land soft.',
      },
      {
        time: 2016,
        text: 'Jete petit. Small brush and hop to the other foot. Land soft, bend small.',
      },
      {
        time: 2040,
        text: 'Pas de chat. Step, lift one knee then the other like a cat, land soft and close.',
      },
      {
        time: 2064,
        text: 'Set: echappe 2, changement 2, glissade right to assemble right, jete petit right, pas de chat right.',
      },
      { time: 2088, text: 'Mirror to the left. Quiet feet, spine tall.' },
      {
        time: 2112,
        text: 'Reminder. Bend small on landings. Shoulders down. Breathe steady.',
      },

      // 50:00–56:00 Waltz travel, balancé, pas de bourrée, chaines, pique
      {
        time: 2136,
        text: 'Waltz step. Step, side, close with a tiny rise. Arms round and soft.',
      },
      {
        time: 2160,
        text: 'Waltz right 2 bars, then left 2 bars. Breathe with the phrase.',
      },
      {
        time: 2184,
        text: 'Balance step. Rock side, back, front with open arms. Add epaulement small.',
      },
      {
        time: 2208,
        text: 'Pas de bourree. Back, side, front. Tiny triangle. Light and quick.',
      },
      {
        time: 2232,
        text: 'Pas de bourree right, then left. Arms low and calm.',
      },
      {
        time: 2256,
        text: 'Chaines are tiny quick turns on two feet. Step close, turn, spot, repeat.',
      },
      {
        time: 2280,
        text: 'Chaines across small to the right 6 counts. Arms in first.',
      },
      {
        time: 2304,
        text: 'Chaines back small to the left 6 counts. Quiet feet.',
      },
      {
        time: 2328,
        text: 'Pique turn. Step on a straight leg to releve, other leg to low passe, spot, close.',
      },
      {
        time: 2352,
        text: 'Pique turns right 4 small. Then left 4 small. Control the close.',
      },

      // 56:00–58:30 Grand allegro prep phrase small and safe
      {
        time: 2376,
        text: 'Tombe means to fall into a lunge. Tombe right, pas de bourree, glissade, grand jete prep small.',
      },
      {
        time: 2400,
        text: 'Grand jete prep. Brush front, jump forward small, land soft, close to fifth.',
      },
      {
        time: 2424,
        text: 'Run the prep phrase to the right once. Knees over toes on land.',
      },
      { time: 2448, text: 'Mirror left side once. Chest calm. Arms soft.' },
      {
        time: 2472,
        text: 'Repeat your best side once more. Control and breathe.',
      },
      {
        time: 2496,
        text: 'Reminder. Land through toes then heels. Spine tall. Quiet feet.',
      },

      // 58:30–60:00 Performance run and cooldown reverence
      {
        time: 2520,
        text: 'Performance flow. Do your petit allegro set, then your travel turns, then your grand allegro prep.',
      },
      {
        time: 2544,
        text: 'Arms smooth and round. Spot your head on all turns.',
      },
      {
        time: 2568,
        text: 'Finish in first. Releve balance for 3, 2, 1. Lower slow.',
      },
      {
        time: 2592,
        text: 'Butterfly stretch. Sit tall, feet together, knees wide, hold ankles. Hold for 3, 2, 1.',
      },
      {
        time: 2616,
        text: 'Seated hamstring fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      {
        time: 2640,
        text: 'Figure four right small. Ankle over knee, sit tall. Hold for 3, 2, 1.',
      },
      {
        time: 2664,
        text: 'Figure four left small. Breathe easy. Hold for 3, 2, 1.',
      },
      {
        time: 2688,
        text: 'Roll to stand. First position. Tall spine. Quiet feet.',
      },
      {
        time: 2712,
        text: 'Grand port de bras slow. Low to first to second to fifth, then down.',
      },
      {
        time: 2736,
        text: 'Reverence. A small bow or curtsey to say thank you. Hold for 3, 2, 1.',
      },
      {
        time: 2760,
        text: 'Posture scan. Head over hips, shoulders down, chin level.',
      },
      {
        time: 2784,
        text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1. Calm body.',
      },
      { time: 2808, text: 'You danced with strength and grace. Small smile.' },
      { time: 2832, text: 'Stand tall in first for 3, 2, 1. Arms low.' },
      { time: 2856, text: 'Deep breath in. Long breath out. Quiet feet.' },

      // Extra gentle spacing to complete 60:00
      { time: 2880, text: 'Hold still. Feel tall and calm for 3, 2, 1.' },
      {
        time: 3000,
        text: 'Reminder. Two tracks, knees and toes match. Shoulders down.',
      },
      { time: 3120, text: 'Arms low to first to second. Breathe smooth.' },
      { time: 3240, text: 'Neck long. Chest calm. Quiet feet.' },
      { time: 3360, text: 'Stand proud. Thank your body for dancing today.' },
      { time: 3480, text: 'Big breath in, long breath out. Calm smile.' },
      {
        time: 3486,
        text: 'Session complete. Complete Ballet finished. Great work today.',
      },
    ],
    timeTotal: 3488,
    equipment: 'None',
  },
];

export default balletDrills;
